import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  CognitoUserPool,
  CognitoUser,
  AuthenticationDetails,
  CognitoIdToken,
  CognitoAccessToken,
  CognitoRefreshToken,
  CognitoUserSession
} from 'amazon-cognito-identity-js';
import AWS from 'aws-sdk';
import yaml from 'js-yaml';
import QRCode from 'qrcode';
import enhancedLicenseManager from './utils/enhancedLicenseManager.js';
import { formatDate, formatDateTime } from './utils/dateFormat';
import { API_URL, USER_POOL_ID, USER_POOL_CLIENT_ID } from './config';
import { is16BitOrganization } from './utils/versionUpdate';

// Installations created before this date predate the post-installation
// checklist in the DMS, so an empty or partial record for them is not a
// problem to flag. Compared against the repo's GitHub creation time.
const POST_INSTALL_FLAG_SINCE = Date.parse('2026-09-01T00:00:00Z');
const createdOnOrAfterCutoff = (createdAt) => {
  const ms = createdAt ? Date.parse(createdAt) : NaN;
  // Unknown creation time (not cached yet) keeps the flag; a refresh fills it in.
  return !Number.isFinite(ms) || ms >= POST_INSTALL_FLAG_SINCE;
};

const userPool = new CognitoUserPool({
  UserPoolId: USER_POOL_ID,
  ClientId: USER_POOL_CLIENT_ID,
});
console.log('CognitoUserPool config:', USER_POOL_ID, USER_POOL_CLIENT_ID);

// Link to the DMS User Guide (DMS-LBL-002). The PDF lives in the version-docs
// S3 bucket (dms-16bit-version-docs) under docs/ and is served publicly via
// CloudFront at /docs/*, so a same-origin relative URL works.
const DMS_USER_GUIDE_URL =
  '/docs/DMS-LBL-002%20DMS%20for%20Distributors_norevhist.docx.pdf';

function getCurrentSession() {
  return new Promise((resolve, reject) => {
    const cognitoUser = userPool.getCurrentUser();
    if (!cognitoUser) return reject('No user');
    cognitoUser.getSession((err, session) => {
      if (err) return reject(err);
      resolve(session);
    });
  });
}

function getUserAttributes(cognitoUser) {
  return new Promise((resolve, reject) => {
    cognitoUser.getUserAttributes((err, attributes) => {
      if (err) return reject(err);
      const email = attributes.find(attr => attr.getName() === 'email')?.getValue();
      resolve({ email });
    });
  });
}

// Decode JWT token to get user groups
function decodeJWT(token) {
  try {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(atob(base64).split('').map(function(c) {
      return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
    }).join(''));
    return JSON.parse(jsonPayload);
  } catch (error) {
    console.error('Error decoding JWT:', error);
    return {};
  }
}

export default function Home() {
  const [repos, setRepos] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [timeout, setTimeoutWarning] = useState(false);
  const [user, setUser] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [loginForm, setLoginForm] = useState({ username: '', password: '' });
  const [loginError, setLoginError] = useState(null);
  const [newPasswordRequired, setNewPasswordRequired] = useState(false);
  const [newPasswordForm, setNewPasswordForm] = useState({ newPassword: '', confirmPassword: '' });
  const [cognitoUser, setCognitoUser] = useState(null);
  // MFA state variables
  const [mfaRequired, setMfaRequired] = useState(false);
  const [mfaCode, setMfaCode] = useState('');
  const [mfaError, setMfaError] = useState(null);
  const [mfaChallengeName, setMfaChallengeName] = useState(null);
  // Forgot password state variables
  const [forgotPasswordStep, setForgotPasswordStep] = useState(null); // 'initiate' | 'confirm' | null
  const [forgotPasswordForm, setForgotPasswordForm] = useState({ 
    email: '', 
    verificationCode: '', 
    newPassword: '', 
    confirmPassword: '' 
  });
  const [forgotPasswordError, setForgotPasswordError] = useState(null);
  // TOTP setup state variables
  const [totpSetupRequired, setTotpSetupRequired] = useState(false);
  const [totpSecretCode, setTotpSecretCode] = useState('');
  const [totpQrCodeUrl, setTotpQrCodeUrl] = useState('');
  const [totpVerificationCode, setTotpVerificationCode] = useState('');
  const [totpSetupError, setTotpSetupError] = useState(null);
  const [licenseStatuses, setLicenseStatuses] = useState({});
  const [institutions, setInstitutions] = useState({});
  const [countries, setCountries] = useState({});
  const [archivedRepos, setArchivedRepos] = useState({});
  // repo -> { status: 'none'|'partial'|'complete', missing: [labels] } from the backend
  const [postInstallation, setPostInstallation] = useState({});
  const [needsPostInstallOnly, setNeedsPostInstallOnly] = useState(false);
  // repo -> self-reported licence usage from Mixpanel (via LicenseUsageSync)
  const [usageByRepo, setUsageByRepo] = useState({});
  const [usageSyncedAt, setUsageSyncedAt] = useState(null);
  const [usageSyncing, setUsageSyncing] = useState(false);
  const [exportingLogs, setExportingLogs] = useState(false);
  const [showArchived, setShowArchived] = useState(false);
  const [checkingLicenses, setCheckingLicenses] = useState(false);
  const [hoveredRepo, setHoveredRepo] = useState(null);
  const [tooltipPosition, setTooltipPosition] = useState({ x: 0, y: 0 });
  const [cacheStats, setCacheStats] = useState(null);
  const [rateLimitInfo, setRateLimitInfo] = useState(null);
  const [licenseSummary, setLicenseSummary] = useState(null);
  const [reposNeedingAttention, setReposNeedingAttention] = useState([]);
  const [isRepoListRateLimited, setIsRepoListRateLimited] = useState(false);
  const [showCreateRepo, setShowCreateRepo] = useState(false);
  const [newRepoName, setNewRepoName] = useState("");
  const [creatingRepo, setCreatingRepo] = useState(false);
  const [createRepoError, setCreateRepoError] = useState(null);
  const [repoNameError, setRepoNameError] = useState('');
  const [refreshing, setRefreshing] = useState(false);
  const [cacheInfo, setCacheInfo] = useState(null);
  const [repoSearch, setRepoSearch] = useState('');
  const [sortConfig, setSortConfig] = useState({ column: 'repo', direction: 'asc' });
  const [menuOpen, setMenuOpen] = useState(false);
  const [columnFilters, setColumnFilters] = useState({
    repo: '',
    institution: '',
    organization: '',
    rhoVersion: '',
    jurisdiction: '',
    country: '',
    stage: '',
    useCase: '',
    status: '',
  });
  const navigate = useNavigate();

  // Function to open create repo modal
  const openCreateRepoModal = () => {
    setShowCreateRepo(true);
    setNewRepoName("");
    setCreateRepoError(null);
    setRepoNameError('');
  };

  // Function to refresh repository cache
  const refreshRepoCache = async () => {
    setRefreshing(true);
    setError(null);
    try {
      console.log('Starting cache refresh...');
      const session = await getCurrentSession();
      const idToken = session.getIdToken().getJwtToken();
      
      console.log('Calling POST /repos/refresh...');
      const response = await fetch(`${API_URL}/repos/refresh?t=${Date.now()}`, {
        method: 'POST',
        headers: {
          Authorization: idToken,
        },
      });
      
      console.log('Response status:', response.status);
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error('Refresh failed:', response.status, errorText);
        throw new Error(`HTTP ${response.status}: ${errorText}`);
      }
      
      const data = await response.json();
      console.log('Cache refresh response:', data);
      
      // Show success message temporarily
      if (data.count !== undefined) {
        console.log(`Successfully refreshed ${data.count} repositories`);
      }
      
      // After successful refresh, reload the repos list
      console.log('Reloading repository list from cache...');
      await listRepos();
    } catch (err) {
      console.error('Cache refresh error:', err);
      setError(`Failed to refresh cache: ${err.message}. Check browser console for details.`);
    } finally {
      setRefreshing(false);
    }
  };

  const listRepos = async () => {
    setLoading(true);
    setError(null);
    setTimeoutWarning(false);
    let timeoutId = setTimeout(() => setTimeoutWarning(true), 10000); // 10s timeout
    try {
      const session = await getCurrentSession();
      const idToken = session.getIdToken().getJwtToken();
      const response = await fetch(`${API_URL}/repos`, {
        method: 'GET',
        headers: {
          Authorization: idToken,
        },
      });
      
      if (!response.ok) {
        const errorText = await response.text();
        // Check for rate limiting error
        if (errorText.includes('API rate limit exceeded') || response.status === 403) {
          setError('GitHub API rate limit exceeded. Repository list cannot be fetched. Please try again later.');
          setIsRepoListRateLimited(true);
          // Set empty repos to show the UI structure
          setRepos({});
          return;
        }
        throw new Error(errorText);
      }
      
      const data = await response.json();
      console.log('API Response:', data);
      
      // Check if cache is empty
      if (data.cacheEmpty) {
        setRepos({});
        setError('Repository cache is empty. Please click "Refresh Cache" to load data from GitHub.');
        return;
      }
      
      let deployments = data.deployments || {};
      setRepos(deployments);
      setInstitutions(data.institutions || {});
      setCountries(data.countries || {});
      setArchivedRepos(data.archived || {});
      setPostInstallation(data.post_installation || {});
      setUsageByRepo(data.usage || {});
      setUsageSyncedAt(data.usage_synced_at || null);
      setIsRepoListRateLimited(false);
      
      // Store cache info if available
      if (data.cacheInfo) {
        setCacheInfo(data.cacheInfo);
      }
      
      // If licenses are included in response, use them.
      // Backend returns snake_case (expires_at, issued_to); frontend expects camelCase.
      if (data.licenses) {
        console.log('License data from cache:', data.licenses);
        const normalized = {};
        for (const [repo, info] of Object.entries(data.licenses)) {
          normalized[repo] = {
            ...info,
            expiresAt: info.expiresAt ?? info.expires_at ?? null,
          };
        }
        setLicenseStatuses(normalized);
        setCheckingLicenses(false);
      } else {
        // Fallback to checking license statuses for all repositories
        await checkLicenseStatuses(deployments, idToken);
      }
    } catch (err) {
      // Check if it's a rate limiting error
      if (err.message && err.message.includes('rate limit')) {
        setError('GitHub API rate limit exceeded. Repository list cannot be fetched. Please try again later.');
        setIsRepoListRateLimited(true);
        // Set empty repos to show the UI structure
        setRepos({});
      } else {
        setError(err.message);
      }
    } finally {
      clearTimeout(timeoutId);
      setLoading(false);
    }
  };

  const checkLicenseStatuses = async (deployments, idToken) => {
    setCheckingLicenses(true);

    try {
      // Get all repositories from all owners
      const allRepos = [];
      Object.entries(deployments).forEach(([owner, repos]) => {
        repos.forEach(repo => {
          allRepos.push(repo);
        });
      });

      // Use enhanced license manager for batch processing
      const statuses = await enhancedLicenseManager.getLicenseStatuses(
        allRepos, 
        API_URL, 
        idToken, 
        {
          useCache: true,
          batchSize: 10,
          showProgress: true
        }
      );

      setLicenseStatuses(statuses);
      
      // Update statistics and summary
      const stats = enhancedLicenseManager.getCacheStats();
      setCacheStats(stats);
      
      const summary = enhancedLicenseManager.getLicenseSummary(statuses);
      setLicenseSummary(summary);
      
      const needsAttention = enhancedLicenseManager.getReposNeedingAttention(statuses);
      setReposNeedingAttention(needsAttention);

      // Get rate limit info if available
      try {
        const rateLimit = await enhancedLicenseManager.getRateLimit();
        setRateLimitInfo(rateLimit);
      } catch (error) {
        console.log('Rate limit info not available');
      }

    } catch (err) {
      console.error('Error checking license statuses:', err);
    } finally {
      setCheckingLicenses(false);
    }
  };

  const getCardStyle = (repo) => {
    const status = licenseStatuses[repo]?.status;
    const baseStyle = {
      border: '1px solid #ccc',
      borderRadius: '8px',
      padding: '16px',
      minWidth: '200px',
      cursor: 'pointer',
      boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
      transition: 'box-shadow 0.2s',
    };

    switch (status) {
      case 'valid':
        return {
          ...baseStyle,
          background: '#d4edda',
          borderColor: '#c3e6cb',
        };
      case 'expired':
        return {
          ...baseStyle,
          background: '#fff3cd',
          borderColor: '#ffeaa7',
        };
      case 'none':
        return {
          ...baseStyle,
          background: '#f8d7da',
          borderColor: '#f5c6cb',
        };
      case 'rate_limited':
        return {
          ...baseStyle,
          background: '#e2e3e5',
          borderColor: '#d6d8db',
        };
      default:
        return {
          ...baseStyle,
          background: '#fafbfc',
        };
    }
  };

  const getLicenseTooltip = (repo) => {
    const status = licenseStatuses[repo];
    if (!status) return 'Checking license status...';

    switch (status.status) {
      case 'valid':
        const validDate = formatDate(status.expiresAt);
        return `Valid license - expires ${validDate}`;
      case 'expired':
        const expiredDate = formatDate(status.expiresAt);
        return `Expired license - expired ${expiredDate}`;
      case 'none':
        return 'No license found';
      case 'rate_limited':
        return 'License status unavailable - GitHub API rate limit exceeded. Please try again later.';
      case 'error':
        return `Error checking license: ${status.error}`;
      default:
        return 'Unknown license status';
    }
  };

  const getStatusIndicator = (repo) => {
    const status = licenseStatuses[repo]?.status;
    if (!status) return null;

    const indicators = {
      valid: '🟢',
      expired: '🟠',
      none: '🔴',
      rate_limited: '⏳',
      error: '⚠️'
    };

    return (
      <span style={{ fontSize: '12px', marginLeft: '8px' }}>
        {indicators[status] || '❓'}
      </span>
    );
  };

  const ROW_PALETTES = {
    valid:        { bg: 'var(--status-valid-bg)',   hover: 'var(--status-valid-bg-hover)',   text: 'var(--status-valid-fg)',   label: 'Valid' },
    expired:      { bg: 'var(--status-expired-bg)', hover: 'var(--status-expired-bg-hover)', text: 'var(--status-expired-fg)', label: 'Expired' },
    none:         { bg: 'var(--status-none-bg)',    hover: 'var(--status-none-bg-hover)',    text: 'var(--status-none-fg)',    label: 'No License' },
    rate_limited: { bg: 'var(--status-neutral-bg)', hover: 'var(--status-neutral-bg-hover)', text: 'var(--status-neutral-fg)', label: 'Rate Limited' },
    error:        { bg: 'var(--status-neutral-bg)', hover: 'var(--status-neutral-bg-hover)', text: 'var(--status-neutral-fg)', label: 'Error' },
    unknown:      { bg: 'transparent',              hover: 'var(--surface-hover)',           text: 'var(--text-secondary)',    label: 'Checking…' },
  };

  const getRowPalette = (repo) => {
    const status = licenseStatuses[repo]?.status;
    return ROW_PALETTES[status] || ROW_PALETTES.unknown;
  };

  const formatExpiry = (repo) => {
    const s = licenseStatuses[repo];
    if (!s || !s.expiresAt) return '—';
    const formatted = formatDate(s.expiresAt);
    return formatted || '—';
  };

  // Does the licence the site is running differ from the one the DMS holds?
  // Compared on the calendar day so time-of-day noise is ignored.
  const siteLicenseDiffers = (row) => {
    const u = row.usage;
    if (!u || !u.license_expires_at || !row.expiresAt) return false;
    const day = (v) => { const d = new Date(v); return isNaN(d) ? null : d.toISOString().slice(0, 10); };
    const a = day(u.license_expires_at); const b = day(row.expiresAt);
    return !!(a && b && a !== b);
  };

  const renderUsageCell = (row) => {
    const u = row.usage;
    if (!u || u.xrays_used === null || u.xrays_used === undefined) {
      return <span style={{ color: 'var(--text-tertiary)' }}>—</span>;
    }
    const unlimited = !u.max_xrays;
    const pct = unlimited ? null : Math.min(100, Math.max(0, u.percent_used ?? (u.xrays_used / u.max_xrays) * 100));
    const barColor = pct === null ? 'var(--status-neutral-fg)' : pct >= 90 ? 'var(--status-none-fg)' : pct >= 75 ? 'var(--status-expired-fg)' : 'var(--status-valid-fg)';
    const differs = siteLicenseDiffers(row);
    const title = [
      `Reported by the site ${u.reported_at ? formatDateTime(u.reported_at) : ''}`.trim(),
      unlimited ? `${u.xrays_used.toLocaleString()} x-rays used, unlimited licence` : `${u.xrays_used.toLocaleString()} of ${u.max_xrays.toLocaleString()} x-rays used (${pct.toFixed(1)}%)`,
      u.valid === false ? `Site reports licence INVALID (${u.reason || 'unknown reason'})` : null,
      differs ? `Site licence ends ${formatDate(u.license_expires_at)}, DMS licence ends ${formatDate(row.expiresAt)}: the site may not have picked up the latest licence` : null,
    ].filter(Boolean).join('\n');
    return (
      <div title={title} style={{ minWidth: '120px' }}>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: '6px', whiteSpace: 'nowrap' }}>
          <span>{u.xrays_used.toLocaleString()}</span>
          <span style={{ color: 'var(--text-tertiary)', fontSize: '12px' }}>/ {unlimited ? '∞' : u.max_xrays.toLocaleString()}</span>
          {pct !== null && <span style={{ fontSize: '12px', color: barColor, fontWeight: 600 }}>{pct.toFixed(0)}%</span>}
          {u.valid === false && <span title="Site reports its licence as invalid" style={{ fontSize: '11px', color: 'var(--status-none-fg)', fontWeight: 700 }}>INVALID</span>}
          {differs && <span style={{ fontSize: '12px', color: 'var(--status-expired-fg)' }} aria-label="Site licence differs from DMS">⚠</span>}
        </div>
        {pct !== null && (
          <div style={{ height: '4px', background: 'var(--surface-3)', borderRadius: '2px', marginTop: '4px', overflow: 'hidden' }}>
            <div style={{ width: `${pct}%`, height: '100%', background: barColor }} />
          </div>
        )}
      </div>
    );
  };

  // Admin: download all service logs across 16 Bit-owned repos as a CSV file.
  const exportServiceLogs = async () => {
    setExportingLogs(true);
    try {
      const session = await getCurrentSession();
      const idToken = session.getIdToken().getJwtToken();
      const res = await fetch(`${API_URL}/service-log/export?scope=16bit&format=csv`, { headers: { Authorization: idToken } });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const disposition = res.headers.get('Content-Disposition') || '';
      const match = disposition.match(/filename="([^"]+)"/);
      const filename = match ? match[1] : `rho-service-logs-${new Date().toISOString().slice(0, 10)}.csv`;
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = filename; document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (err) {
      console.error('Service log export failed:', err);
      setError(`Could not export service logs: ${err.message}`);
    } finally {
      setExportingLogs(false);
    }
  };

  // Admin: ask LicenseUsageSync to pull fresh numbers from Mixpanel now.
  const syncUsageNow = async () => {
    setUsageSyncing(true);
    try {
      const session = await getCurrentSession();
      const idToken = session.getIdToken().getJwtToken();
      const res = await fetch(`${API_URL}/license-usage/sync`, { method: 'POST', headers: { Authorization: idToken } });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      // The sync runs asynchronously (~15 s); reload the table afterwards.
      setTimeout(() => { listRepos(); setUsageSyncing(false); }, 20000);
    } catch (err) {
      console.error('Usage sync failed:', err);
      setUsageSyncing(false);
    }
  };

  const formatDaysRemaining = (repo) => {
    const s = licenseStatuses[repo];
    if (!s || !s.expiresAt) return '—';
    const ms = new Date(s.expiresAt).getTime() - Date.now();
    if (isNaN(ms)) return '—';
    const days = Math.round(ms / 86400000);
    if (days > 0) return `in ${days} day${days === 1 ? '' : 's'}`;
    if (days < 0) return `${Math.abs(days)} day${days === -1 ? '' : 's'} ago`;
    return 'today';
  };

  // Build a flat list of installation rows from the per-org deployments map.
  // The backend already filters which orgs/repos this user can see, so we
  // simply flatten what we received. For 16bit team this includes every org.
  const buildInstallationRows = () => {
    const rows = [];
    Object.entries(repos).forEach(([organization, ownerRepos]) => {
      (ownerRepos || []).forEach((repo) => {
        const info = licenseStatuses[repo] || {};
        const postInstall = postInstallation[repo] || { status: 'none', missing: [] };
        const createdAt = info.created_at || info.createdAt || null;
        rows.push({
          repo,
          institution: institutions[repo] || '',
          archived: !!archivedRepos[repo],
          postInstall,
          // The checklist is a 16 Bit process: only repos 16 Bit owns are
          // flagged. Archived installs are frozen, and installs that predate the
          // checklist (created before POST_INSTALL_FLAG_SINCE) are never flagged.
          needsPostInstall: is16BitOrganization(organization)
            && !archivedRepos[repo]
            && postInstall.status !== 'complete'
            && createdOnOrAfterCutoff(createdAt),
          usage: usageByRepo[repo] || null,
          // GitHub repository creation time (ISO), captured on cache refresh.
          createdAt,
          organization: organization || '—',
          rhoVersion: info.target_revision || info.targetRevision || '',
          jurisdiction: info.jurisdiction || '',
          country: countries[repo] || '',
          stage: info.stage || '',
          useCase: info.use_case || info.useCase || '',
          statusKey: info.status || 'unknown',
          statusLabel: (ROW_PALETTES[info.status] || ROW_PALETTES.unknown).label,
          expiresAt: info.expiresAt || null,
        });
      });
    });
    return rows;
  };

  const sortValueFor = (row, column) => {
    switch (column) {
      case 'repo':
        return (row.repo || '').toLowerCase();
      case 'institution':
        return (row.institution || '').toLowerCase();
      case 'organization':
        return (row.organization || '').toLowerCase();
      case 'rhoVersion':
        return (row.rhoVersion || '').toLowerCase();
      case 'jurisdiction':
        return (row.jurisdiction || '').toLowerCase();
      case 'country':
        return (row.country || '').toLowerCase();
      case 'stage':
        return (row.stage || '').toLowerCase();
      case 'useCase':
        return (row.useCase || '').toLowerCase();
      case 'status':
        return (row.statusLabel || 'zzz').toLowerCase();
      case 'created': {
        const ms = row.createdAt ? new Date(row.createdAt).getTime() : NaN;
        return Number.isFinite(ms) ? ms : Number.POSITIVE_INFINITY;
      }
      case 'expires':
      case 'remaining': {
        const ms = row.expiresAt ? new Date(row.expiresAt).getTime() : NaN;
        return Number.isFinite(ms) ? ms : Number.POSITIVE_INFINITY;
      }
      case 'usage': {
        // Percent of volume used; unlimited licences sort by raw count after
        // every metered one; repos with no report sort last.
        const u = row.usage;
        if (!u || u.xrays_used === null || u.xrays_used === undefined) return Number.NEGATIVE_INFINITY;
        if (u.percent_used !== null && u.percent_used !== undefined) return u.percent_used;
        return -1 + u.xrays_used / 1e9;
      }
      default:
        return (row.repo || '').toLowerCase();
    }
  };

  const sortRows = (rows) => {
    const { column, direction } = sortConfig;
    const factor = direction === 'asc' ? 1 : -1;
    return [...rows].sort((a, b) => {
      const va = sortValueFor(a, column);
      const vb = sortValueFor(b, column);
      if (va < vb) return -1 * factor;
      if (va > vb) return 1 * factor;
      return a.repo.localeCompare(b.repo);
    });
  };

  const toggleSort = (column) => {
    setSortConfig((prev) =>
      prev.column === column
        ? { column, direction: prev.direction === 'asc' ? 'desc' : 'asc' }
        : { column, direction: 'asc' }
    );
  };

  const sortIndicator = (column) => {
    if (sortConfig.column !== column) return <span style={{ opacity: 0.3, marginLeft: 6, fontSize: '10px' }}>↕</span>;
    return <span style={{ marginLeft: 6, fontSize: '10px', color: 'var(--accent)' }}>{sortConfig.direction === 'asc' ? '▲' : '▼'}</span>;
  };

  // Free-text global search: matches any of the visible columns.
  const matchesGlobalSearch = (row) => {
    const q = repoSearch.trim().toLowerCase();
    if (!q) return true;
    return [row.repo, row.institution, row.organization, row.rhoVersion, row.jurisdiction, row.country, row.stage, row.useCase, row.statusLabel]
      .some((v) => (v || '').toString().toLowerCase().includes(q));
  };

  // Per-column filters. Dropdown filters (organization/rhoVersion/jurisdiction/stage/status)
  // are exact matches; free-form filters (repo) are substring matches.
  const matchesColumnFilters = (row) => {
    const f = columnFilters;
    if (f.repo && !row.repo.toLowerCase().includes(f.repo.trim().toLowerCase())) return false;
    if (f.institution && !(row.institution || '').toLowerCase().includes(f.institution.trim().toLowerCase())) return false;
    if (f.organization && row.organization !== f.organization) return false;
    if (f.rhoVersion && row.rhoVersion !== f.rhoVersion) return false;
    if (f.jurisdiction && row.jurisdiction !== f.jurisdiction) return false;
    if (f.country && row.country !== f.country) return false;
    if (f.stage && row.stage !== f.stage) return false;
    if (f.useCase && row.useCase !== f.useCase) return false;
    // "archived" is a status filter spanning a separate axis from statusKey.
    if (f.status === 'archived') { if (!row.archived) return false; }
    else if (f.status && row.statusKey !== f.status) return false;
    return true;
  };

  const uniqueValues = (rows, key) => {
    const set = new Set();
    rows.forEach((r) => {
      const v = r[key];
      if (v) set.add(v);
    });
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  };

  const handleMouseEnter = (repo, event) => {
    setHoveredRepo(repo);
    setTooltipPosition({
      x: event.clientX + 10,
      y: event.clientY - 10
    });
  };

  const handleMouseLeave = () => {
    setHoveredRepo(null);
  };

  useEffect(() => {
    // Try to get current session and user on mount
    const cognitoUser = userPool.getCurrentUser();
    if (cognitoUser) {
      cognitoUser.getSession(async (err, session) => {
        if (!err && session.isValid()) {
          try {
            const { email } = await getUserAttributes(cognitoUser);
            setUser({ username: email });
            
            // Check if user is admin
            const idToken = session.getIdToken().getJwtToken();
            const decodedToken = decodeJWT(idToken);
            const groups = decodedToken['cognito:groups'] || [];
            const adminStatus = groups.includes('Admin');
            setIsAdmin(adminStatus);
            
            console.log('User groups:', groups);
            console.log('Is admin:', adminStatus);
            console.log('Decoded token:', decodedToken);
            
            // Initialize enhanced license manager
            enhancedLicenseManager.initialize();
            
            // Set up event listeners
            const unsubscribeLicenseUpdate = enhancedLicenseManager.onLicenseUpdate((results) => {
              console.log('License statuses updated:', results);
              setLicenseStatuses(prev => ({ ...prev, ...results }));
              
              // Update summary and stats
              const summary = enhancedLicenseManager.getLicenseSummary({ ...licenseStatuses, ...results });
              setLicenseSummary(summary);
              
              const needsAttention = enhancedLicenseManager.getReposNeedingAttention({ ...licenseStatuses, ...results });
              setReposNeedingAttention(needsAttention);
            });
            
            const unsubscribeError = enhancedLicenseManager.onError((error) => {
              console.error('License manager error:', error);
              setError(error.message);
            });
            
            listRepos();
            
            // Cleanup function
            return () => {
              unsubscribeLicenseUpdate();
              unsubscribeError();
              enhancedLicenseManager.stopBackgroundRefresh();
            };
          } catch (attrErr) {
            console.error('Error getting user attributes:', attrErr);
            setUser({ username: cognitoUser.getUsername() });
            listRepos();
          }
        }
      });
    }
    // eslint-disable-next-line
  }, []);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginError(null);
    const { username, password } = loginForm;
    
    // Use AWS SDK directly with USER_PASSWORD_AUTH to bypass SRP issues
    const cognitoIdentityServiceProvider = new AWS.CognitoIdentityServiceProvider({
      region: 'ca-central-1'
    });
    
    try {
      console.log('Attempting direct AWS SDK authentication with USER_PASSWORD_AUTH...');
      
      const authParams = {
        AuthFlow: 'USER_PASSWORD_AUTH',
        ClientId: USER_POOL_CLIENT_ID,
        AuthParameters: {
          USERNAME: username,
          PASSWORD: password,
        },
      };
      
      const authResult = await cognitoIdentityServiceProvider.initiateAuth(authParams).promise();
      console.log('Auth result:', authResult);
      
      // Handle different challenge types
      if (authResult.ChallengeName === 'NEW_PASSWORD_REQUIRED') {
        console.log('New password required');
        // Store the challenge session for password completion
        window.challengeSession = authResult.Session;
        window.challengeName = authResult.ChallengeName;
        window.challengeUsername = username;
        
        const userData = { Username: username, Pool: userPool };
        const user = new CognitoUser(userData);
        setCognitoUser(user);
        setNewPasswordRequired(true);
        setLoginError(null);
        return;
      }
      
      if (authResult.ChallengeName === 'EMAIL_MFA' || authResult.ChallengeName === 'EMAIL_OTP' || authResult.ChallengeName === 'SOFTWARE_TOKEN_MFA') {
        console.log('MFA Required:', authResult.ChallengeName);
        // Store the challenge session for MFA completion
        window.challengeSession = authResult.Session;
        window.challengeName = authResult.ChallengeName;
        window.challengeUsername = username;
        
        const userData = { Username: username, Pool: userPool };
        const user = new CognitoUser(userData);
        setCognitoUser(user);
        setMfaRequired(true);
        setMfaChallengeName(authResult.ChallengeName);
        setLoginError(null);
        setMfaError(null);
        return;
      }

      if (authResult.ChallengeName === 'MFA_SETUP') {
        
        // Get the actual Cognito username from challenge parameters
        const cognitoUsername = authResult.ChallengeParameters?.USER_ID_FOR_SRP || username;
        
        // Store the challenge session for MFA setup
        window.challengeSession = authResult.Session;
        window.challengeName = authResult.ChallengeName;
        window.challengeUsername = cognitoUsername; // Use the actual Cognito username
        window.userEmail = username; // Store the email separately for display
        
        // Start TOTP setup flow
        await handleTotpSetupStartWithSession(username, authResult.Session);
        return;
      }
      
      // Success - we have tokens
      if (authResult.AuthenticationResult) {
        console.log('Authentication successful');
        const accessToken = authResult.AuthenticationResult.AccessToken;
        const idToken = authResult.AuthenticationResult.IdToken;
        
        // Check if user needs to set up TOTP
        try {
          const mfaOptions = await cognitoIdentityServiceProvider.getUserMFAOptions({
            AccessToken: accessToken
          }).promise();
          
          console.log('User MFA options:', mfaOptions);
          
          // If user has no MFA options configured, they need to set up TOTP
          if (!mfaOptions.MFAOptions || mfaOptions.MFAOptions.length === 0) {
            console.log('User needs to set up TOTP');
            window.tempAccessToken = accessToken;
            window.challengeUsername = username;
            handleTotpSetupStart(username);
            return;
          }
        } catch (mfaCheckError) {
          console.log('Could not check MFA options, proceeding with login:', mfaCheckError);
        }
        
        // Create a proper session and user object
        const userData = { Username: username, Pool: userPool };
        const user = new CognitoUser(userData);
        
        // Create proper session tokens
        const idTokenObj = new CognitoIdToken({ IdToken: idToken });
        const accessTokenObj = new CognitoAccessToken({ AccessToken: accessToken });
        const refreshTokenObj = new CognitoRefreshToken({ RefreshToken: authResult.AuthenticationResult.RefreshToken || '' });
        
        // Create the session object
        const sessionObj = new CognitoUserSession({
          IdToken: idTokenObj,
          AccessToken: accessTokenObj,
          RefreshToken: refreshTokenObj
        });
        
        // Set the session on the user
        user.setSignInUserSession(sessionObj);

        try {
          const { email } = await getUserAttributes(user);
          setUser({ username: email });
        } catch (attrErr) {
          console.error('Error getting user attributes:', attrErr);
          setUser({ username });
        }
        
        setLoginForm({ username: '', password: '' });
        setNewPasswordRequired(false);
        setMfaRequired(false);
        setMfaCode('');
        setMfaError(null);
        setCognitoUser(null);
        listRepos();
        return;
      }
      
    } catch (error) {
      console.error('AWS SDK authentication error:', error);
      setLoginError(error.message || 'Authentication failed. Please check your credentials and try again.');
      setCognitoUser(null);
      return;
    }
  };

  // Remove the old fallback method entirely to prevent SRP issues
  const handleLoginOld = (e) => {
    console.log('Old authentication method disabled to prevent SRP errors');
    setLoginError('Authentication method not available. Please refresh and try again.');
    return;
  };

  const handleLoginFallback = (e) => {
    // This is kept as a backup but should not be used
    e.preventDefault();
    setLoginError(null);
    const { username, password } = loginForm;
    const userData = {
      Username: username,
      Pool: userPool,
    };
    const user = new CognitoUser(userData);
    setCognitoUser(user);
    const authDetails = new AuthenticationDetails({
      Username: username,
      Password: password,
    });
    try {
      user.authenticateUser(authDetails, {
        onSuccess: async (result) => {
          console.log('Login onSuccess called with result:', result);
          try {
            const { email } = await getUserAttributes(user);
            setUser({ username: email });
          } catch (attrErr) {
            console.error('Error getting user attributes:', attrErr);
            setUser({ username });
          }
          setLoginForm({ username: '', password: '' });
          setNewPasswordRequired(false);
          setMfaRequired(false);
          setMfaCode('');
          setMfaError(null);
          setCognitoUser(null);
          listRepos();
        },
        onFailure: (err) => {
          console.error('Login onFailure called with error:', err);
          
          // Check if this is the NewDeviceMetadata error
          if (err && err.message && err.message.includes('NewDeviceMetadata')) {
            console.log('Detected NewDeviceMetadata error - this might be a Cognito User Pool configuration issue');
            setLoginError('Authentication error: Device tracking configuration issue. Please contact support.');
          } else {
            setLoginError(err.message || 'Login failed');
          }
          setMfaRequired(false);
          setCognitoUser(null);
        },
        newPasswordRequired: (userAttributes, requiredAttributes) => {
          console.log('newPasswordRequired callback called');
          setNewPasswordRequired(true);
          setLoginError(null);
        },
        mfaRequired: (challengeName, challengeParameters) => {
          console.log('MFA Required:', challengeName, challengeParameters);
          setMfaRequired(true);
          setMfaChallengeName(challengeName);
          setLoginError(null);
          setMfaError(null);
        },
        newDeviceMetadata: (userAttributes, requiredAttributes) => {
          console.log('newDeviceMetadata called during login - userAttributes:', userAttributes);
          console.log('newDeviceMetadata called during login - requiredAttributes:', requiredAttributes);
          console.log('Attempting to complete login success flow from newDeviceMetadata...');
          
          try {
            getUserAttributes(user).then(({ email }) => {
              console.log('Got user email from login newDeviceMetadata:', email);
              setUser({ username: email });
              setLoginForm({ username: '', password: '' });
              setNewPasswordRequired(false);
              setMfaRequired(false);
              setMfaCode('');
              setMfaError(null);
              setCognitoUser(null);
              listRepos();
            }).catch((attrErr) => {
              console.error('Error getting user attributes in login newDeviceMetadata:', attrErr);
              setUser({ username });
              setLoginForm({ username: '', password: '' });
              setNewPasswordRequired(false);
              setMfaRequired(false);
              setMfaCode('');
              setMfaError(null);
              setCognitoUser(null);
              listRepos();
            });
          } catch (error) {
            console.error('Error in login newDeviceMetadata callback:', error);
            setUser({ username });
            setLoginForm({ username: '', password: '' });
            setNewPasswordRequired(false);
            setMfaRequired(false);
            setMfaCode('');
            setMfaError(null);
            setCognitoUser(null);
            listRepos();
          }
        }
      });
    } catch (authError) {
      console.error('Error in authenticateUser call:', authError);
      if (authError && authError.message && authError.message.includes('NewDeviceMetadata')) {
        setLoginError('Device tracking configuration error. Please contact support.');
      } else {
        setLoginError('Authentication failed. Please try again.');
      }
      setCognitoUser(null);
    }
  };

  const handleNewPassword = async (e) => {
    e.preventDefault();
    setLoginError(null);
    const { newPassword, confirmPassword } = newPasswordForm;
    
    if (newPassword !== confirmPassword) {
      setLoginError('Passwords do not match');
      return;
    }
    
    if (newPassword.length < 8) {
      setLoginError('Password must be at least 8 characters long');
      return;
    }
    
    // Try AWS SDK approach first
    if (window.challengeSession && window.challengeName === 'NEW_PASSWORD_REQUIRED') {
      console.log('Setting new password via AWS SDK...');
      
      const cognitoIdentityServiceProvider = new AWS.CognitoIdentityServiceProvider({
        region: 'ca-central-1'
      });
      
      try {
        const challengeParams = {
          ClientId: USER_POOL_CLIENT_ID,
          ChallengeName: 'NEW_PASSWORD_REQUIRED',
          Session: window.challengeSession,
          ChallengeResponses: {
            USERNAME: window.challengeUsername,
            NEW_PASSWORD: newPassword
          }
        };
        
        const result = await cognitoIdentityServiceProvider.respondToAuthChallenge(challengeParams).promise();
        console.log('New password challenge response:', result);
        
        if (result.AuthenticationResult) {
          console.log('New password set successfully');
          const accessToken = result.AuthenticationResult.AccessToken;
          const idToken = result.AuthenticationResult.IdToken;
          
          // Create a proper session and user object
          const userData = { Username: window.challengeUsername, Pool: userPool };
          const user = new CognitoUser(userData);
          
          // Create proper session tokens
          const idTokenObj = new CognitoIdToken({ IdToken: idToken });
          const accessTokenObj = new CognitoAccessToken({ AccessToken: accessToken });
          const refreshTokenObj = new CognitoRefreshToken({ RefreshToken: result.AuthenticationResult.RefreshToken || '' });
          
          // Create the session object
          const sessionObj = new CognitoUserSession({
            IdToken: idTokenObj,
            AccessToken: accessTokenObj,
            RefreshToken: refreshTokenObj
          });
          
          // Set the session on the user
          user.setSignInUserSession(sessionObj);
          
          try {
            const { email } = await getUserAttributes(user);
            setUser({ username: email });
          } catch (attrErr) {
            console.error('Error getting user attributes:', attrErr);
            setUser({ username: window.challengeUsername });
          }
          
          setNewPasswordForm({ newPassword: '', confirmPassword: '' });
          setNewPasswordRequired(false);
          setCognitoUser(null);
          
          // Clean up challenge data
          delete window.challengeSession;
          delete window.challengeName;
          delete window.challengeUsername;
          
          listRepos();
          return;
        }
        
        // Check if there's another challenge (like MFA)
        if (result.ChallengeName) {
          console.log('Another challenge required after password:', result.ChallengeName);
          window.challengeSession = result.Session;
          window.challengeName = result.ChallengeName;
          
          if (result.ChallengeName === 'EMAIL_MFA' || result.ChallengeName === 'EMAIL_OTP' || result.ChallengeName === 'SOFTWARE_TOKEN_MFA') {
            setNewPasswordRequired(false);
            setMfaRequired(true);
            setMfaChallengeName(result.ChallengeName);
            setMfaError(null);
            return;
          }
        }
        
      } catch (error) {
        console.error('AWS SDK new password error:', error);
        setLoginError(error.message || 'Failed to set new password. Please try again.');
        return;
      }
    }
    
    // Fallback to old method if needed (should rarely be used now)
    if (cognitoUser) {
      console.log('Falling back to amazon-cognito-identity-js for new password...');
      cognitoUser.completeNewPasswordChallenge(newPassword, {}, {
        onSuccess: async (result) => {
          try {
            const { email } = await getUserAttributes(cognitoUser);
            setUser({ username: email });
          } catch (attrErr) {
            console.error('Error getting user attributes:', attrErr);
            setUser({ username: cognitoUser.getUsername() });
          }
          setNewPasswordForm({ newPassword: '', confirmPassword: '' });
          setNewPasswordRequired(false);
          setCognitoUser(null);
          listRepos();
        },
        onFailure: (err) => {
          setLoginError(err.message || 'Failed to set new password');
        }
      });
    } else {
      setLoginError('Authentication session lost. Please try logging in again.');
    }
  };

  const handleMfaSubmit = async (e) => {
    e.preventDefault();
    setMfaError(null);
    
    if (!mfaCode.trim()) {
      setMfaError('Please enter the verification code');
      return;
    }
    
    // Try AWS SDK approach first
    if (window.challengeSession && window.challengeName) {
      console.log('Submitting MFA code via AWS SDK...');
      
      const cognitoIdentityServiceProvider = new AWS.CognitoIdentityServiceProvider({
        region: 'ca-central-1'
      });
      
      try {
        const challengeParams = {
          ClientId: USER_POOL_CLIENT_ID,
          ChallengeName: window.challengeName,
          Session: window.challengeSession,
          ChallengeResponses: {
            USERNAME: window.challengeUsername,
            EMAIL_MFA_CODE: mfaCode,
            EMAIL_OTP_CODE: mfaCode,
            SOFTWARE_TOKEN_MFA_CODE: mfaCode
          }
        };
        
        const result = await cognitoIdentityServiceProvider.respondToAuthChallenge(challengeParams).promise();
        console.log('MFA challenge response:', result);
        
        if (result.AuthenticationResult) {
          console.log('MFA authentication successful');
          const accessToken = result.AuthenticationResult.AccessToken;
          const idToken = result.AuthenticationResult.IdToken;
          
          // Create a proper session and user object
          const userData = { Username: window.challengeUsername, Pool: userPool };
          const user = new CognitoUser(userData);
          
          // Create proper session tokens
          const idTokenObj = new CognitoIdToken({ IdToken: idToken });
          const accessTokenObj = new CognitoAccessToken({ AccessToken: accessToken });
          const refreshTokenObj = new CognitoRefreshToken({ RefreshToken: result.AuthenticationResult.RefreshToken || '' });
          
          // Create the session object
          const sessionObj = new CognitoUserSession({
            IdToken: idTokenObj,
            AccessToken: accessTokenObj,
            RefreshToken: refreshTokenObj
          });
          
          // Set the session on the user
          user.setSignInUserSession(sessionObj);
          
          try {
            const { email } = await getUserAttributes(user);
            setUser({ username: email });
          } catch (attrErr) {
            console.error('Error getting user attributes:', attrErr);
            setUser({ username: window.challengeUsername });
          }
          
          setLoginForm({ username: '', password: '' });
          setMfaRequired(false);
          setMfaCode('');
          setMfaError(null);
          setCognitoUser(null);
          
          // Clean up challenge data
          delete window.challengeSession;
          delete window.challengeName;
          delete window.challengeUsername;
          
          listRepos();
          return;
        }
        
      } catch (error) {
        console.error('AWS SDK MFA error:', error);
        setMfaError(error.message || 'Invalid verification code. Please try again.');
        return;
      }
    }
    
    // Fallback to old method
    if (cognitoUser) {
      console.log('Falling back to amazon-cognito-identity-js for MFA...');
      console.log('Submitting MFA code:', mfaCode);
      console.log('CognitoUser object:', cognitoUser);
      
      try {
        cognitoUser.sendMFACode(mfaCode, {
          onSuccess: async (result) => {
            console.log('MFA Success result:', result);
            try {
              const { email } = await getUserAttributes(cognitoUser);
              setUser({ username: email });
            } catch (attrErr) {
              console.error('Error getting user attributes:', attrErr);
              setUser({ username: cognitoUser.getUsername() });
            }
            setLoginForm({ username: '', password: '' });
            setMfaRequired(false);
            setMfaCode('');
            setMfaError(null);
            setCognitoUser(null);
            listRepos();
          },
          onFailure: (err) => {
            console.error('MFA verification failed:', err);
            console.error('Error details:', JSON.stringify(err, null, 2));
            setMfaError(err.message || 'Invalid verification code. Please try again.');
          },
          newDeviceMetadata: (userAttributes, requiredAttributes) => {
            console.log('newDeviceMetadata called during MFA - userAttributes:', userAttributes);
            console.log('newDeviceMetadata called during MFA - requiredAttributes:', requiredAttributes);
            console.log('Attempting to complete MFA success flow from newDeviceMetadata...');
            
            // Try to complete the success flow from here
            try {
              getUserAttributes(cognitoUser).then(({ email }) => {
                console.log('Got user email from newDeviceMetadata:', email);
                setUser({ username: email });
                setLoginForm({ username: '', password: '' });
                setMfaRequired(false);
                setMfaCode('');
                setMfaError(null);
                setCognitoUser(null);
                listRepos();
              }).catch((attrErr) => {
                console.error('Error getting user attributes in newDeviceMetadata:', attrErr);
                setUser({ username: cognitoUser.getUsername() });
                setLoginForm({ username: '', password: '' });
                setMfaRequired(false);
                setMfaCode('');
                setMfaError(null);
                setCognitoUser(null);
                listRepos();
              });
            } catch (error) {
              console.error('Error in newDeviceMetadata callback:', error);
              // Fallback
              setUser({ username: cognitoUser.getUsername() });
              setLoginForm({ username: '', password: '' });
              setMfaRequired(false);
              setMfaCode('');
              setMfaError(null);
              setCognitoUser(null);
              listRepos();
            }
          }
        });
      } catch (error) {
        console.error('Error calling sendMFACode:', error);
        setMfaError('Failed to submit verification code. Please try again.');
      }
    } else {
      console.error('No cognitoUser available for MFA submission');
      setMfaError('Authentication session lost. Please try logging in again.');
    }
  };

  const handleLogout = () => {
    const cognitoUser = userPool.getCurrentUser();
    if (cognitoUser) cognitoUser.signOut();
    setUser(null);
    setRepos({});
    setNewPasswordRequired(false);
    setMfaRequired(false);
    setMfaCode('');
    setMfaError(null);
    setCognitoUser(null);
    // Reset forgot password state
    setForgotPasswordStep(null);
    setForgotPasswordForm({ email: '', verificationCode: '', newPassword: '', confirmPassword: '' });
    setForgotPasswordError(null);
    // Reset TOTP setup state
    setTotpSetupRequired(false);
    setTotpSecretCode('');
    setTotpQrCodeUrl('');
    setTotpVerificationCode('');
    setTotpSetupError(null);
    // Clean up temporary data
    delete window.tempAccessToken;
    delete window.challengeUsername;
    delete window.challengeSession;
    delete window.challengeName;
    delete window.userEmail;
  };

  // Forgot Password Handlers
  const handleForgotPasswordInitiate = async (e) => {
    e.preventDefault();
    setForgotPasswordError(null);
    const { email } = forgotPasswordForm;
    
    if (!email.trim()) {
      setForgotPasswordError('Please enter your email address');
      return;
    }
    
    const cognitoIdentityServiceProvider = new AWS.CognitoIdentityServiceProvider({
      region: 'ca-central-1'
    });
    
    try {
      console.log('Initiating forgot password for email:', email);
      
      const params = {
        ClientId: USER_POOL_CLIENT_ID,
        Username: email,
      };
      
      await cognitoIdentityServiceProvider.forgotPassword(params).promise();
      console.log('Forgot password initiated successfully');
      
      // Move to confirmation step
      setForgotPasswordStep('confirm');
      setForgotPasswordError(null);
      
    } catch (error) {
      console.error('Forgot password initiation error:', error);
      if (error.code === 'UserNotFoundException') {
        setForgotPasswordError('No account found with this email address');
      } else if (error.code === 'InvalidParameterException') {
        setForgotPasswordError('Invalid email address');
      } else if (error.code === 'LimitExceededException') {
        setForgotPasswordError('Too many requests. Please try again later');
      } else {
        setForgotPasswordError(error.message || 'Failed to send password reset code. Please try again.');
      }
    }
  };

  const handleForgotPasswordConfirm = async (e) => {
    e.preventDefault();
    setForgotPasswordError(null);
    const { email, verificationCode, newPassword, confirmPassword } = forgotPasswordForm;
    
    // Validation
    if (!verificationCode.trim()) {
      setForgotPasswordError('Please enter the verification code');
      return;
    }
    
    if (!newPassword) {
      setForgotPasswordError('Please enter a new password');
      return;
    }
    
    if (newPassword.length < 8) {
      setForgotPasswordError('Password must be at least 8 characters long');
      return;
    }
    
    if (newPassword !== confirmPassword) {
      setForgotPasswordError('Passwords do not match');
      return;
    }
    
    const cognitoIdentityServiceProvider = new AWS.CognitoIdentityServiceProvider({
      region: 'ca-central-1'
    });
    
    try {
      console.log('Confirming forgot password for email:', email);
      
      const params = {
        ClientId: USER_POOL_CLIENT_ID,
        Username: email,
        ConfirmationCode: verificationCode,
        Password: newPassword,
      };
      
      await cognitoIdentityServiceProvider.confirmForgotPassword(params).promise();
      console.log('Password reset successful');
      
      // Reset forgot password state and show success
      setForgotPasswordStep(null);
      setForgotPasswordForm({ email: '', verificationCode: '', newPassword: '', confirmPassword: '' });
      setForgotPasswordError(null);
      
      // Show success message and return to login
      setLoginError(null);
      alert('Password reset successful! You can now log in with your new password.');
      
    } catch (error) {
      console.error('Forgot password confirmation error:', error);
      if (error.code === 'CodeMismatchException') {
        setForgotPasswordError('Invalid verification code. Please check and try again');
      } else if (error.code === 'ExpiredCodeException') {
        setForgotPasswordError('Verification code has expired. Please request a new one');
      } else if (error.code === 'InvalidPasswordException') {
        setForgotPasswordError('Password does not meet requirements. Please try a different password');
      } else if (error.code === 'LimitExceededException') {
        setForgotPasswordError('Too many attempts. Please try again later');
      } else {
        setForgotPasswordError(error.message || 'Failed to reset password. Please try again.');
      }
    }
  };

  const handleForgotPasswordCancel = () => {
    setForgotPasswordStep(null);
    setForgotPasswordForm({ email: '', verificationCode: '', newPassword: '', confirmPassword: '' });
    setForgotPasswordError(null);
  };

  // TOTP Setup Handlers
  const handleTotpSetupStart = async (username) => {
    const cognitoIdentityServiceProvider = new AWS.CognitoIdentityServiceProvider({
      region: 'ca-central-1'
    });

    try {
      console.log('Starting TOTP setup for user:', username);
      
      const params = {
        AccessToken: window.tempAccessToken // We'll need to store this during login
      };
      
      const result = await cognitoIdentityServiceProvider.associateSoftwareToken(params).promise();
      console.log('TOTP setup initiated:', result);
      
      setTotpSecretCode(result.SecretCode);
      
      // Generate QR code URL
      const qrCodeData = `otpauth://totp/DMS:${username}?secret=${result.SecretCode}&issuer=16bit.ai`;
      const qrCodeUrl = await QRCode.toDataURL(qrCodeData);
      setTotpQrCodeUrl(qrCodeUrl);
      
      setTotpSetupRequired(true);
      setTotpSetupError(null);
      
    } catch (error) {
      console.error('TOTP setup error:', error);
      setTotpSetupError(error.message || 'Failed to start TOTP setup. Please try again.');
    }
  };

  // TOTP Setup with Session (for MFA_SETUP challenge)
  const handleTotpSetupStartWithSession = async (username, session) => {
    const cognitoIdentityServiceProvider = new AWS.CognitoIdentityServiceProvider({
      region: 'ca-central-1'
    });

    try {
      const params = {
        Session: session
      };
      
      const result = await cognitoIdentityServiceProvider.associateSoftwareToken(params).promise();
      
      // IMPORTANT: Update the session with the new session from the response
      if (result.Session) {
        window.challengeSession = result.Session;
      }
      
      setTotpSecretCode(result.SecretCode);
      
      // Generate QR code URL
      const qrCodeData = `otpauth://totp/DMS:${username}?secret=${result.SecretCode}&issuer=16bit.ai`;
      const qrCodeUrl = await QRCode.toDataURL(qrCodeData);
      setTotpQrCodeUrl(qrCodeUrl);
      
      setTotpSetupRequired(true);
      setTotpSetupError(null);
      
    } catch (error) {
      console.error('TOTP setup error:', error);
      setTotpSetupError(error.message || 'Failed to start TOTP setup. Please try again.');
    }
  };

  const handleTotpSetupComplete = async () => {
    const cognitoIdentityServiceProvider = new AWS.CognitoIdentityServiceProvider({
      region: 'ca-central-1'
    });

    try {
      if (window.challengeName === 'MFA_SETUP') {
        // For MFA_SETUP challenge, we need to first verify the software token, then respond to the challenge
        const verifyParams = {
          Session: window.challengeSession,
          UserCode: totpVerificationCode
        };
        
        const verifyResult = await cognitoIdentityServiceProvider.verifySoftwareToken(verifyParams).promise();
        
        // Step 2: Respond to the MFA_SETUP challenge
        const challengeParams = {
          ClientId: USER_POOL_CLIENT_ID,
          ChallengeName: 'MFA_SETUP',
          Session: verifyResult.Session || window.challengeSession, // Use updated session if provided
          ChallengeResponses: {
            USERNAME: window.challengeUsername,
            SOFTWARE_TOKEN_MFA_CODE: totpVerificationCode
          }
        };
        
        const result = await cognitoIdentityServiceProvider.respondToAuthChallenge(challengeParams).promise();
        
        if (result.AuthenticationResult) {
          
          // Create proper session tokens
          const idTokenObj = new CognitoIdToken({ IdToken: result.AuthenticationResult.IdToken });
          const accessTokenObj = new CognitoAccessToken({ AccessToken: result.AuthenticationResult.AccessToken });
          const refreshTokenObj = new CognitoRefreshToken({ RefreshToken: result.AuthenticationResult.RefreshToken || '' });
          
          // Create the session object
          const sessionObj = new CognitoUserSession({
            IdToken: idTokenObj,
            AccessToken: accessTokenObj,
            RefreshToken: refreshTokenObj
          });
          
          // Create user and set session
          const userData = { Username: window.challengeUsername, Pool: userPool };
          const user = new CognitoUser(userData);
          user.setSignInUserSession(sessionObj);
          
          try {
            const { email } = await getUserAttributes(user);
            setUser({ username: email });
          } catch (attrErr) {
            console.error('Error getting user attributes:', attrErr);
            setUser({ username: window.challengeUsername });
          }
          
          // Reset TOTP setup state
          setTotpSetupRequired(false);
          setTotpSecretCode('');
          setTotpQrCodeUrl('');
          setTotpVerificationCode('');
          setTotpSetupError(null);
          
          // Clean up temporary data
          delete window.challengeSession;
          delete window.challengeName;
          delete window.challengeUsername;
          delete window.userEmail;
          
          listRepos();
          return;
        }
      } else {
        // For regular access token based setup
        const params = {
          AccessToken: window.tempAccessToken,
          UserCode: totpVerificationCode
        };
        
        await cognitoIdentityServiceProvider.verifySoftwareToken(params).promise();
        console.log('TOTP setup completed successfully');
        
        // Set TOTP as preferred MFA
        await cognitoIdentityServiceProvider.setUserMFAPreference({
          AccessToken: window.tempAccessToken,
          SoftwareTokenMfaSettings: {
            Enabled: true,
            PreferredMfa: true
          }
        }).promise();
        
        // Reset TOTP setup state
        setTotpSetupRequired(false);
        setTotpSecretCode('');
        setTotpQrCodeUrl('');
        setTotpVerificationCode('');
        setTotpSetupError(null);
        
        // Complete login process
        const userData = { Username: window.challengeUsername, Pool: userPool };
        const user = new CognitoUser(userData);
        
        try {
          const { email } = await getUserAttributes(user);
          setUser({ username: email });
        } catch (attrErr) {
          console.error('Error getting user attributes:', attrErr);
          setUser({ username: window.challengeUsername });
        }
        
        // Clean up temporary data
        delete window.tempAccessToken;
        delete window.challengeUsername;
        
        listRepos();
      }
      
    } catch (error) {
      console.error('TOTP verification error:', error);
      if (error.code === 'InvalidParameterException') {
        setTotpSetupError('Invalid verification code. Please check your authenticator app and try again.');
      } else if (error.code === 'CodeMismatchException') {
        setTotpSetupError('Invalid verification code. Please enter the current code from your authenticator app.');
      } else {
        setTotpSetupError(error.message || 'Failed to verify TOTP code. Please try again.');
      }
    }
  };

  const handleTotpSetupCancel = () => {
    setTotpSetupRequired(false);
    setTotpSecretCode('');
    setTotpQrCodeUrl('');
    setTotpVerificationCode('');
    setTotpSetupError(null);
    
    // Clean up temporary data
    delete window.tempAccessToken;
    delete window.challengeUsername;
    delete window.challengeSession;
    delete window.challengeName;
    delete window.userEmail;
  };

  // Validate repo name format
  const validateRepoName = (name) => {
    if (!name) return 'Repository name is required';
    if (!/^[a-z0-9-]+$/.test(name)) {
      return 'Repository name must contain only lowercase letters, numbers, and dashes';
    }
    if (name.startsWith('-') || name.endsWith('-')) {
      return 'Repository name cannot start or end with a dash';
    }
    if (name.includes('--')) {
      return 'Repository name cannot contain consecutive dashes';
    }
    if (name.length < 2) {
      return 'Repository name must be at least 2 characters long';
    }
    if (name.length > 50) {
      return 'Repository name cannot exceed 50 characters';
    }
    
    // Check if repo name already exists
    const fullRepoName = `rho-customer-${name}`;
    const allRepos = Object.values(repos).flat();
    if (allRepos.some(repo => repo.name === fullRepoName)) {
      return 'Repository name already exists';
    }
    
    return '';
  };

  // Handle repo name input change with validation
  const handleRepoNameChange = (e) => {
    const value = e.target.value.toLowerCase(); // Force lowercase
    setNewRepoName(value);
    
    // Clear error when user starts typing
    if (repoNameError) {
      setRepoNameError('');
    }
    
    // Validate when user stops typing (debounced)
    if (value) {
      setTimeout(() => {
        if (value === newRepoName) { // Only validate if value hasn't changed
          const error = validateRepoName(value);
          if (error) {
            setRepoNameError(error);
          }
        }
      }, 500); // 500ms delay
    }
  };

  // Create repo handler
  const handleCreateRepo = async (e) => {
    e.preventDefault();
    
    // Validate repo name
    const validationError = validateRepoName(newRepoName);
    if (validationError) {
      setRepoNameError(validationError);
      return;
    }
    
    setCreatingRepo(true);
    setCreateRepoError(null);
    setRepoNameError('');
    try {
      const session = await getCurrentSession();
      const idToken = session.getIdToken().getJwtToken();
      const response = await fetch(`${API_URL}/repos`, {
        method: 'POST',
        headers: {
          Authorization: idToken,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ customerId: newRepoName }),
      });
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(errorText);
      }
      setShowCreateRepo(false);
      setNewRepoName("");
      listRepos();
    } catch (err) {
      setCreateRepoError(err.message);
    } finally {
      setCreatingRepo(false);
    }
  };

  // Modal backdrop and dialog styles
  const modalBackdropStyle = {
    position: 'fixed',
    top: 0,
    left: 0,
    width: '100vw',
    height: '100vh',
    background: 'rgba(15, 13, 13, 0.72)',
    backdropFilter: 'blur(6px)',
    WebkitBackdropFilter: 'blur(6px)',
    zIndex: 1000,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  };
  const modalDialogStyle = {
    background: 'var(--surface-1)',
    border: '1px solid var(--border)',
    borderRadius: '12px',
    padding: '32px 28px',
    minWidth: '360px',
    boxShadow: '0 24px 64px -16px rgba(0,0,0,0.6), 0 0 0 1px rgba(255,255,255,0.02) inset',
    display: 'flex',
    flexDirection: 'column',
    gap: '18px',
    position: 'relative',
    color: 'var(--text-primary)',
  };

  // Shared style for items inside the account dropdown menu.
  const menuItemStyle = {
    textAlign: 'left',
    width: '100%',
    background: 'transparent',
    border: 'none',
    borderRadius: '6px',
    padding: '10px 12px',
    cursor: 'pointer',
    fontWeight: 600,
    fontSize: '14px',
    color: 'var(--text-primary)',
    transition: 'background-color 0.15s',
  };

  if (newPasswordRequired) {
    return (
      <div style={{ padding: 20 }}>
        {/* Header with Logo and Title */}
        <div style={{ 
          display: 'flex', 
          alignItems: 'center', 
          marginBottom: '30px',
          paddingBottom: '20px',
          borderBottom: '1px solid var(--border)'
        }}>
          <img 
            src="/16bit-primary-logo-reverse.svg" 
            alt="16bit.ai Logo" 
            style={{ 
              height: '50px', 
              marginRight: '20px' 
            }} 
          />
          <div>
            <h1 style={{ 
              margin: 0, 
              fontSize: '2.5rem', 
              fontWeight: 'bold',
              color: 'var(--text-primary)'
            }}>
              DMS
            </h1>
            <p style={{ 
              margin: '5px 0 0 0', 
              color: 'var(--text-secondary)',
              fontSize: '1rem'
            }}>
              Distributor Management System 
            </p>
          </div>
        </div>
        
        <h2>Set New Password</h2>
        <form onSubmit={handleNewPassword} style={{ maxWidth: 300 }}>
          <label>New Password:<br />
            <input
              type="password"
              value={newPasswordForm.newPassword}
              onChange={e => setNewPasswordForm({ ...newPasswordForm, newPassword: e.target.value })}
              style={{ width: '100%' }}
              placeholder="Enter new password"
            />
          </label><br /><br />
          <label>Confirm Password:<br />
            <input
              type="password"
              value={newPasswordForm.confirmPassword}
              onChange={e => setNewPasswordForm({ ...newPasswordForm, confirmPassword: e.target.value })}
              style={{ width: '100%' }}
              placeholder="Confirm new password"
            />
          </label><br /><br />
          <button type="submit">Set New Password</button>
        </form>
        {loginError && <div style={{ color: 'var(--status-none-fg)', marginTop: 10 }}>{loginError}</div>}
      </div>
    );
  }

  if (mfaRequired) {
    return (
      <div style={{ padding: 20 }}>
        {/* Header with Logo and Title */}
        <div style={{ 
          display: 'flex', 
          alignItems: 'center', 
          marginBottom: '30px',
          paddingBottom: '20px',
          borderBottom: '1px solid var(--border)'
        }}>
          <img 
            src="/16bit-primary-logo-reverse.svg" 
            alt="16bit.ai Logo" 
            style={{ 
              height: '50px', 
              marginRight: '20px' 
            }} 
          />
          <div>
            <h1 style={{ 
              margin: 0, 
              fontSize: '2.5rem', 
              fontWeight: 'bold',
              color: 'var(--text-primary)'
            }}>
              DMS
            </h1>
            <p style={{ 
              margin: '5px 0 0 0', 
              color: 'var(--text-secondary)',
              fontSize: '1rem'
            }}>
              Distributor Management System
            </p>
          </div>
        </div>
        
        <h2>Two-Factor Authentication</h2>
        <p style={{ marginBottom: '20px', color: 'var(--text-secondary)' }}>
          A verification code has been sent to your email address. Please enter the code below to complete your login.
        </p>
        <form onSubmit={handleMfaSubmit} style={{ maxWidth: 300 }}>
          <label>Verification Code:<br />
            <input
              type="text"
              value={mfaCode}
              onChange={e => setMfaCode(e.target.value)}
              style={{ 
                width: '100%',
                padding: '10px',
                fontSize: '16px',
                textAlign: 'center',
                letterSpacing: '2px'
              }}
              placeholder="Enter 6-digit code"
              maxLength="6"
              autoComplete="one-time-code"
            />
          </label><br /><br />
          <button 
            type="submit" 
            style={{
              width: '100%',
              padding: '12px',
              backgroundColor: 'var(--color-bit-blue)',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              fontSize: '16px',
              cursor: 'pointer'
            }}
          >
            Verify Code
          </button>
        </form>
        {mfaError && <div style={{ color: 'var(--status-none-fg)', marginTop: 10 }}>{mfaError}</div>}
        <div style={{ marginTop: '20px', textAlign: 'center' }}>
          <button
            type="button"
            onClick={() => {
              setMfaRequired(false);
              setMfaCode('');
              setMfaError(null);
              setCognitoUser(null);
            }}
            style={{
              background: 'none',
              border: 'none',
              color: '#007bff',
              textDecoration: 'underline',
              cursor: 'pointer',
              fontSize: '14px'
            }}
          >
            Back to Login
          </button>
        </div>
      </div>
    );
  }

  // Forgot Password - Initiate Step
  if (forgotPasswordStep === 'initiate') {
    return (
      <div style={{ padding: 20 }}>
        {/* Header with Logo and Title */}
        <div style={{ 
          display: 'flex', 
          alignItems: 'center', 
          marginBottom: '30px',
          paddingBottom: '20px',
          borderBottom: '1px solid var(--border)'
        }}>
          <img 
            src="/16bit-primary-logo-reverse.svg" 
            alt="16bit.ai Logo" 
            style={{ 
              height: '50px', 
              marginRight: '20px' 
            }} 
          />
          <div>
            <h1 style={{ 
              margin: 0, 
              fontSize: '2.5rem', 
              fontWeight: 'bold',
              color: 'var(--text-primary)'
            }}>
              DMS
            </h1>
            <p style={{ 
              margin: '5px 0 0 0', 
              color: 'var(--text-secondary)',
              fontSize: '1rem'
            }}>
              Distributor Management System
            </p>
          </div>
        </div>
        
        <h2>Reset Password</h2>
        <p style={{ marginBottom: '20px', color: 'var(--text-secondary)', maxWidth: 400 }}>
          Enter your email address and we'll send you a verification code to reset your password.
        </p>
        <form onSubmit={handleForgotPasswordInitiate} style={{ maxWidth: 400 }}>
          <label>Email Address:<br />
            <input
              type="email"
              value={forgotPasswordForm.email}
              onChange={e => setForgotPasswordForm({ ...forgotPasswordForm, email: e.target.value })}
              style={{ 
                width: '100%',
                padding: '10px',
                fontSize: '16px',
                border: '1px solid #ccc',
                borderRadius: '4px',
                marginTop: '5px'
              }}
              placeholder="Enter your email address"
              required
            />
          </label><br /><br />
          <div style={{ display: 'flex', gap: '10px' }}>
            <button 
              type="submit"
              style={{
                padding: '12px 24px',
                backgroundColor: 'var(--color-bit-blue)',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: '16px',
                fontWeight: 'bold'
              }}
            >
              Send Reset Code
            </button>
            <button 
              type="button"
              onClick={handleForgotPasswordCancel}
              style={{
                padding: '12px 24px',
                backgroundColor: 'var(--surface-3)',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: '16px'
              }}
            >
              Cancel
            </button>
          </div>
        </form>
        {forgotPasswordError && <div style={{ color: 'var(--status-none-fg)', marginTop: 15, maxWidth: 400 }}>{forgotPasswordError}</div>}
      </div>
    );
  }

  // Forgot Password - Confirm Step
  if (forgotPasswordStep === 'confirm') {
    return (
      <div style={{ padding: 20 }}>
        {/* Header with Logo and Title */}
        <div style={{ 
          display: 'flex', 
          alignItems: 'center', 
          marginBottom: '30px',
          paddingBottom: '20px',
          borderBottom: '1px solid var(--border)'
        }}>
          <img 
            src="/16bit-primary-logo-reverse.svg" 
            alt="16bit.ai Logo" 
            style={{ 
              height: '50px', 
              marginRight: '20px' 
            }} 
          />
          <div>
            <h1 style={{ 
              margin: 0, 
              fontSize: '2.5rem', 
              fontWeight: 'bold',
              color: 'var(--text-primary)'
            }}>
              DMS
            </h1>
            <p style={{ 
              margin: '5px 0 0 0', 
              color: 'var(--text-secondary)',
              fontSize: '1rem'
            }}>
              Distributor Management System
            </p>
          </div>
        </div>
        
        <h2>Enter Reset Code</h2>
        <p style={{ marginBottom: '20px', color: 'var(--text-secondary)', maxWidth: 400 }}>
          We've sent a verification code to <strong>{forgotPasswordForm.email}</strong>. 
          Enter the code and your new password below.
        </p>
        <form onSubmit={handleForgotPasswordConfirm} style={{ maxWidth: 400 }}>
          <label>Verification Code:<br />
            <input
              type="text"
              value={forgotPasswordForm.verificationCode}
              onChange={e => setForgotPasswordForm({ ...forgotPasswordForm, verificationCode: e.target.value })}
              style={{ 
                width: '100%',
                padding: '10px',
                fontSize: '16px',
                border: '1px solid #ccc',
                borderRadius: '4px',
                marginTop: '5px',
                textAlign: 'center',
                letterSpacing: '2px'
              }}
              placeholder="Enter 6-digit code"
              maxLength="6"
              required
            />
          </label><br /><br />
          <label>New Password:<br />
            <input
              type="password"
              value={forgotPasswordForm.newPassword}
              onChange={e => setForgotPasswordForm({ ...forgotPasswordForm, newPassword: e.target.value })}
              style={{ 
                width: '100%',
                padding: '10px',
                fontSize: '16px',
                border: '1px solid #ccc',
                borderRadius: '4px',
                marginTop: '5px'
              }}
              placeholder="Enter new password"
              minLength="8"
              required
            />
          </label><br /><br />
          <label>Confirm New Password:<br />
            <input
              type="password"
              value={forgotPasswordForm.confirmPassword}
              onChange={e => setForgotPasswordForm({ ...forgotPasswordForm, confirmPassword: e.target.value })}
              style={{ 
                width: '100%',
                padding: '10px',
                fontSize: '16px',
                border: '1px solid #ccc',
                borderRadius: '4px',
                marginTop: '5px'
              }}
              placeholder="Confirm new password"
              minLength="8"
              required
            />
          </label><br /><br />
          <div style={{ display: 'flex', gap: '10px' }}>
            <button 
              type="submit"
              style={{
                padding: '12px 24px',
                backgroundColor: 'var(--color-discovery)',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: '16px',
                fontWeight: 'bold'
              }}
            >
              Reset Password
            </button>
            <button 
              type="button"
              onClick={handleForgotPasswordCancel}
              style={{
                padding: '12px 24px',
                backgroundColor: 'var(--surface-3)',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: '16px'
              }}
            >
              Cancel
            </button>
          </div>
        </form>
        {forgotPasswordError && <div style={{ color: 'var(--status-none-fg)', marginTop: 15, maxWidth: 400 }}>{forgotPasswordError}</div>}
        <div style={{ marginTop: '20px', textAlign: 'left', maxWidth: 400 }}>
          <button
            type="button"
            onClick={() => setForgotPasswordStep('initiate')}
            style={{
              background: 'none',
              border: 'none',
              color: '#007bff',
              textDecoration: 'underline',
              cursor: 'pointer',
              fontSize: '14px'
            }}
          >
            ← Back to email entry
          </button>
        </div>
      </div>
    );
  }

  // TOTP Setup Screen
  if (totpSetupRequired) {
    return (
      <div style={{ padding: 20 }}>
        {/* Header with Logo and Title */}
        <div style={{ 
          display: 'flex', 
          alignItems: 'center', 
          marginBottom: '30px',
          paddingBottom: '20px',
          borderBottom: '1px solid var(--border)'
        }}>
          <img 
            src="/16bit-primary-logo-reverse.svg" 
            alt="16bit.ai Logo" 
            style={{ 
              height: '50px', 
              marginRight: '20px' 
            }} 
          />
          <div>
            <h1 style={{ 
              margin: 0, 
              fontSize: '2.5rem', 
              fontWeight: 'bold',
              color: 'var(--text-primary)'
            }}>
              DMS
            </h1>
            <p style={{ 
              margin: '5px 0 0 0', 
              color: 'var(--text-secondary)',
              fontSize: '1rem'
            }}>
              Distributor Management System
            </p>
          </div>
        </div>
        
        <h2>Set Up Two-Factor Authentication</h2>
        <p style={{ marginBottom: '20px', color: 'var(--text-secondary)', maxWidth: 500 }}>
          For security, you need to set up two-factor authentication using an authenticator app like Google Authenticator, Authy, or Microsoft Authenticator.
        </p>
        
        <div style={{ maxWidth: 500 }}>
          <h3>Step 1: Scan QR Code</h3>
          <p style={{ color: 'var(--text-secondary)', marginBottom: '15px' }}>
            Open your authenticator app and scan this QR code:
          </p>
          
          {totpQrCodeUrl && (
            <div style={{ textAlign: 'center', marginBottom: '20px' }}>
              <img
                src={totpQrCodeUrl}
                alt="TOTP QR Code"
                style={{
                  border: '1px solid var(--border)',
                  borderRadius: '10px',
                  padding: '12px',
                  backgroundColor: '#f4f1e9',
                }}
              />
            </div>
          )}
          
          <h3>Step 2: Enter Verification Code</h3>
          <p style={{ color: 'var(--text-secondary)', marginBottom: '15px' }}>
            Enter the 6-digit code from your authenticator app:
          </p>
          
          <form onSubmit={(e) => { e.preventDefault(); handleTotpSetupComplete(); }}>
            <input
              type="text"
              value={totpVerificationCode}
              onChange={e => setTotpVerificationCode(e.target.value)}
              style={{ 
                width: '200px',
                padding: '12px',
                fontSize: '18px',
                border: '1px solid #ccc',
                borderRadius: '4px',
                textAlign: 'center',
                letterSpacing: '3px',
                marginBottom: '20px'
              }}
              placeholder="000000"
              maxLength="6"
              required
            />
            <br />
            
            <div style={{ display: 'flex', gap: '10px' }}>
              <button 
                type="submit"
                disabled={totpVerificationCode.length !== 6}
                style={{
                  padding: '12px 24px',
                  backgroundColor: totpVerificationCode.length === 6 ? 'var(--accent)' : 'var(--surface-3)',
                  color: totpVerificationCode.length === 6 ? 'var(--accent-fg)' : 'var(--text-secondary)',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: totpVerificationCode.length === 6 ? 'pointer' : 'not-allowed',
                  fontSize: '14px',
                  fontWeight: 700,
                }}
              >
                Complete Setup
              </button>
              <button 
                type="button"
                onClick={handleTotpSetupCancel}
                style={{
                  padding: '12px 24px',
                  backgroundColor: 'var(--surface-3)',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '16px'
                }}
              >
                Cancel
              </button>
            </div>
          </form>
          
          {totpSetupError && (
            <div style={{ color: 'var(--status-none-fg)', marginTop: 15, padding: '12px 14px', backgroundColor: 'var(--status-none-bg)', borderRadius: '8px', border: '1px solid rgba(255, 158, 171, 0.22)' }}>
              {totpSetupError}
            </div>
          )}

          <div style={{ marginTop: '30px', padding: '16px 18px', backgroundColor: 'var(--surface-1)', borderRadius: '10px', border: '1px solid var(--border)' }}>
            <h4 style={{ margin: '0 0 10px 0', color: 'var(--color-bit-blue)' }}>◇ Tips</h4>
            <ul style={{ margin: 0, paddingLeft: '20px', color: 'var(--text-secondary)' }}>
              <li>Make sure your phone's time is accurate</li>
              <li>Keep your authenticator app installed and backed up</li>
              <li>You'll need this app every time you log in</li>
            </ul>
          </div>
        </div>
      </div>
    );
  }

  if (!user) {
    return (
      <div style={{ padding: 20 }}>
        {/* Header with Logo and Title */}
        <div style={{ 
          display: 'flex', 
          alignItems: 'center', 
          marginBottom: '30px',
          paddingBottom: '20px',
          borderBottom: '1px solid var(--border)'
        }}>
          <img 
            src="/16bit-primary-logo-reverse.svg" 
            alt="16bit.ai Logo" 
            style={{ 
              height: '50px', 
              marginRight: '20px' 
            }} 
          />
          <div>
            <h1 style={{ 
              margin: 0, 
              fontSize: '2.5rem', 
              fontWeight: 'bold',
              color: 'var(--text-primary)'
            }}>
              DMS
            </h1>
            <p style={{ 
              margin: '5px 0 0 0', 
              color: 'var(--text-secondary)',
              fontSize: '1rem'
            }}>
              Distributor Management System
            </p>
          </div>
        </div>
        
        <h2>Sign In</h2>
        <form onSubmit={handleLogin} style={{ maxWidth: 300 }}>
          <label>Username:<br />
            <input
              type="text"
              value={loginForm.username}
              onChange={e => setLoginForm({ ...loginForm, username: e.target.value })}
              style={{ width: '100%' }}
            />
          </label><br /><br />
          <label>Password:<br />
            <input
              type="password"
              value={loginForm.password}
              onChange={e => setLoginForm({ ...loginForm, password: e.target.value })}
              style={{ width: '100%' }}
            />
          </label><br /><br />
          <button type="submit">Sign In</button>
        </form>
        {loginError && <div style={{ color: 'var(--status-none-fg)', marginTop: 10 }}>{loginError}</div>}
        
        {/* Forgot Password Link */}
        <div style={{ marginTop: '20px', textAlign: 'center', maxWidth: 300 }}>
          <button
            type="button"
            onClick={() => {
              setForgotPasswordStep('initiate');
              setForgotPasswordForm({ ...forgotPasswordForm, email: loginForm.username });
              setForgotPasswordError(null);
            }}
            style={{
              background: 'none',
              border: 'none',
              color: '#007bff',
              textDecoration: 'underline',
              cursor: 'pointer',
              fontSize: '14px'
            }}
          >
            Forgot your password?
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={{ padding: 20, position: 'relative' }}>
      {/* Header with Logo, Title, and Actions */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: '20px',
        marginBottom: '30px',
        paddingBottom: '20px',
        borderBottom: '1px solid var(--color-dark-canvas)',
        flexWrap: 'wrap',
      }}>
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <img
            src="/16bit-primary-logo-reverse.svg"
            alt="16 Bit Logo"
            style={{ height: '44px', marginRight: '24px' }}
          />
          <div style={{ borderLeft: '1px solid var(--border)', paddingLeft: '24px' }}>
            <div style={{
              fontSize: '10px',
              fontWeight: 600,
              letterSpacing: '0.18em',
              textTransform: 'uppercase',
              color: 'var(--accent)',
              marginBottom: '4px',
            }}>
              Distributor Management System
            </div>
            <h1 style={{ margin: 0, fontSize: '2rem', fontWeight: 700, color: 'var(--text-primary)', letterSpacing: '-0.02em', lineHeight: 1 }}>
              DMS
            </h1>
          </div>
        </div>

        <div style={{ position: 'relative' }}>
          <button
            onClick={() => setMenuOpen((o) => !o)}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '10px',
              padding: '8px 14px',
              background: menuOpen ? 'var(--surface-hover)' : 'transparent',
              color: 'var(--text-primary)',
              border: '1px solid var(--border-strong)',
              borderRadius: '8px',
              cursor: 'pointer',
              fontWeight: 600,
              fontSize: '14px',
              transition: 'background-color 0.2s, border-color 0.2s',
            }}
          >
            <span style={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '28px',
              height: '28px',
              borderRadius: '50%',
              background: 'var(--color-bit-blue)',
              color: 'white',
              fontSize: '13px',
              fontWeight: 700,
              textTransform: 'uppercase',
            }}>
              {(user.username || '?').charAt(0)}
            </span>
            <span style={{ maxWidth: '220px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {user.username}
            </span>
            <span style={{ fontSize: '10px', color: 'var(--text-secondary)', transform: menuOpen ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }}>▼</span>
          </button>

          {menuOpen && (
            <>
              {/* Click-away backdrop */}
              <div
                onClick={() => setMenuOpen(false)}
                style={{ position: 'fixed', inset: 0, zIndex: 40 }}
              />
              <div style={{
                position: 'absolute',
                top: 'calc(100% + 8px)',
                right: 0,
                minWidth: '260px',
                background: 'var(--surface-1)',
                border: '1px solid var(--border)',
                borderRadius: '10px',
                boxShadow: '0 16px 48px -12px rgba(0,0,0,0.6)',
                padding: '8px',
                zIndex: 50,
                display: 'flex',
                flexDirection: 'column',
                gap: '2px',
              }}>
                <div style={{
                  padding: '8px 12px 10px',
                  borderBottom: '1px solid var(--border)',
                  marginBottom: '4px',
                }}>
                  <div style={{ fontSize: '11px', color: 'var(--text-tertiary)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                    Signed in as
                  </div>
                  <div style={{ fontSize: '13px', color: 'var(--text-primary)', fontWeight: 600, wordBreak: 'break-all' }}>
                    {user.username}
                  </div>
                  {isAdmin && (
                    <div style={{ marginTop: '4px', fontSize: '11px', color: 'var(--accent)', fontWeight: 600 }}>
                      16 Bit Admin
                    </div>
                  )}
                </div>

                <button
                  onClick={() => { setMenuOpen(false); navigate('/organizations'); }}
                  style={menuItemStyle}
                  onMouseOver={(e) => e.currentTarget.style.backgroundColor = 'var(--surface-hover)'}
                  onMouseOut={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                >
                  Manage Organizations
                </button>

                {isAdmin && (
                  <button
                    onClick={() => { setMenuOpen(false); exportServiceLogs(); }}
                    disabled={exportingLogs}
                    style={{ ...menuItemStyle, opacity: exportingLogs ? 0.6 : 1 }}
                    title="Download every service-log entry for 16 Bit-owned installations as a CSV, with institution, jurisdiction, Rho version and UDI"
                    onMouseOver={(e) => e.currentTarget.style.backgroundColor = 'var(--surface-hover)'}
                    onMouseOut={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                  >
                    {exportingLogs ? 'Preparing export…' : 'Export Service Logs (CSV)'}
                  </button>
                )}

                <a
                  href={DMS_USER_GUIDE_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => setMenuOpen(false)}
                  style={{ ...menuItemStyle, textDecoration: 'none', display: 'block' }}
                  onMouseOver={(e) => e.currentTarget.style.backgroundColor = 'var(--surface-hover)'}
                  onMouseOut={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                >
                  Download DMS User Guide
                </a>

                <div style={{ borderTop: '1px solid var(--border)', margin: '4px 0' }} />

                <button
                  onClick={() => { setMenuOpen(false); handleLogout(); }}
                  style={{ ...menuItemStyle, color: 'var(--status-none-fg)' }}
                  onMouseOver={(e) => e.currentTarget.style.backgroundColor = 'var(--surface-hover)'}
                  onMouseOut={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                >
                  Sign Out
                </button>
              </div>
            </>
          )}
        </div>
      </div>

      <div style={{ margin: "20px 0" }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '10px' }}>
            <h2 style={{ margin: 0, fontSize: '1.5rem' }}>Your Repositories</h2>
            <button
              onClick={refreshRepoCache}
              disabled={refreshing || loading}
              style={{
                padding: '8px 14px',
                background: 'transparent',
                color: 'var(--text-secondary)',
                border: '1px solid var(--border-strong)',
                borderRadius: '6px',
                cursor: (refreshing || loading) ? 'not-allowed' : 'pointer',
                fontWeight: 600,
                fontSize: '13px',
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                opacity: (refreshing || loading) ? 0.5 : 1,
                transition: 'border-color 0.2s, color 0.2s',
              }}
              title="Fetch latest data from GitHub and update cache"
            >
              <span style={{ fontSize: '13px' }}>↻</span>
              {refreshing ? 'Refreshing from GitHub…' : 'Refresh Cache'}
            </button>
            {isAdmin && (
              <button
                onClick={syncUsageNow}
                disabled={usageSyncing || loading}
                style={{
                  padding: '8px 14px',
                  background: 'transparent',
                  color: 'var(--text-secondary)',
                  border: '1px solid var(--border-strong)',
                  borderRadius: '6px',
                  cursor: (usageSyncing || loading) ? 'not-allowed' : 'pointer',
                  fontWeight: 600,
                  fontSize: '13px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  opacity: (usageSyncing || loading) ? 0.5 : 1,
                  transition: 'border-color 0.2s, color 0.2s',
                }}
                title={`Pull each site's self-reported x-ray usage from Mixpanel now${usageSyncedAt ? ` (last sync ${formatDateTime(usageSyncedAt)})` : ''}`}
              >
                <span style={{ fontSize: '13px' }}>⇣</span>
                {usageSyncing ? 'Syncing usage…' : 'Sync Usage'}
              </button>
            )}
            {isAdmin && (
            <button
              onClick={() => navigate('/update-versions')}
              disabled={loading}
              style={{
                padding: '8px 14px',
                background: 'transparent',
                color: 'var(--text-secondary)',
                border: '1px solid var(--border-strong)',
                borderRadius: '6px',
                cursor: loading ? 'not-allowed' : 'pointer',
                fontWeight: 600,
                fontSize: '13px',
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                opacity: loading ? 0.5 : 1,
                transition: 'border-color 0.2s, color 0.2s',
              }}
              title="Move several of your installations to a chosen Rho release at once"
            >
              <span style={{ fontSize: '13px' }}>⇪</span>
              Update Versions
            </button>
            )}
            <button
              onClick={listRepos}
              disabled={loading || refreshing}
              style={{
                padding: '8px 14px',
                background: 'transparent',
                color: 'var(--text-secondary)',
                border: '1px solid var(--border-strong)',
                borderRadius: '6px',
                cursor: (loading || refreshing) ? 'not-allowed' : 'pointer',
                fontWeight: 600,
                fontSize: '13px',
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                opacity: (loading || refreshing) ? 0.5 : 1,
              }}
              title="Reload data from cache"
            >
              <span style={{ fontSize: '13px' }}>⟲</span>
              {loading ? 'Loading…' : 'Reload'}
            </button>
            {/* Create Repo Button — primary CTA in Insight */}
            <button
              onClick={openCreateRepoModal}
              style={{
                padding: '8px 16px',
                background: 'var(--accent)',
                color: 'var(--accent-fg)',
                border: 'none',
                borderRadius: '6px',
                cursor: 'pointer',
                fontWeight: 700,
                fontSize: '13px',
                letterSpacing: '0.02em',
                boxShadow: '0 0 0 1px rgba(209, 255, 22, 0.3), 0 4px 16px -4px var(--accent-glow)',
              }}
            >
              + Create Repo
            </button>
            {/* Cache Info Display */}
            {cacheInfo && cacheInfo.lastUpdated && (
              <div style={{
                padding: '6px 12px',
                backgroundColor: 'var(--surface-2)',
                borderRadius: '6px',
                border: '1px solid var(--border)',
                fontSize: '12px',
                color: 'var(--text-secondary)',
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
              }}>
                <span style={{ color: 'var(--color-bit-blue)' }}>●</span>
                <span>
                  Cache updated {formatDateTime(cacheInfo.lastUpdated)}
                </span>
              </div>
            )}
          </div>

          {/* Enhanced Statistics Panel */}
          <div style={{ display: 'flex', gap: '8px', fontSize: '12px' }}>
            {cacheStats && (
              <div style={{
                padding: '6px 10px',
                backgroundColor: 'var(--surface-2)',
                borderRadius: '6px',
                border: '1px solid var(--border)',
                color: 'var(--text-secondary)',
              }}>
                Cache: <strong style={{ color: 'var(--text-primary)' }}>{cacheStats.cacheHits}/{cacheStats.totalRequests}</strong> hits
              </div>
            )}
            {rateLimitInfo && (() => {
              const remaining = (rateLimitInfo.rate?.remaining ?? rateLimitInfo.remaining);
              const limit = (rateLimitInfo.rate?.limit ?? rateLimitInfo.limit);
              const low = remaining < 100;
              return (
                <div style={{
                  padding: '6px 10px',
                  backgroundColor: low ? 'var(--status-none-bg)' : 'var(--status-valid-bg)',
                  borderRadius: '6px',
                  border: `1px solid ${low ? 'rgba(255, 158, 171, 0.25)' : 'rgba(141, 240, 193, 0.22)'}`,
                  color: low ? 'var(--status-none-fg)' : 'var(--status-valid-fg)',
                }}>
                  API: {remaining}/{limit} remaining
                </div>
              );
            })()}
            {licenseSummary && (
              <div style={{
                padding: '6px 10px',
                backgroundColor: 'var(--surface-2)',
                borderRadius: '6px',
                border: '1px solid var(--border)',
                color: 'var(--text-secondary)',
                display: 'flex',
                gap: '10px',
                alignItems: 'center',
              }}>
                <span><span style={{ color: 'var(--status-valid-fg)' }}>●</span> {licenseSummary.valid}</span>
                <span><span style={{ color: 'var(--status-expired-fg)' }}>●</span> {licenseSummary.expired}</span>
                <span><span style={{ color: 'var(--status-none-fg)' }}>●</span> {licenseSummary.none}</span>
                {licenseSummary.rate_limited > 0 && <span><span style={{ color: 'var(--text-tertiary)' }}>●</span> {licenseSummary.rate_limited}</span>}
              </div>
            )}
          </div>
        </div>
        {error && (
          <div style={{
            color: 'var(--status-none-fg)',
            margin: '14px 0',
            padding: '16px 18px',
            backgroundColor: 'var(--status-none-bg)',
            borderRadius: '10px',
            border: '1px solid rgba(255, 158, 171, 0.22)',
          }}>
            <strong style={{ color: 'var(--text-primary)' }}>Error:</strong> {error}
            {isRepoListRateLimited && (
              <div style={{ marginTop: '12px' }}>
                <button
                  onClick={listRepos}
                  style={{
                    padding: '8px 14px',
                    background: 'var(--status-none-fg)',
                    color: '#3a0e1a',
                    border: 'none',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    fontSize: '12px',
                    fontWeight: 700,
                  }}
                >
                  Retry Now
                </button>
              </div>
            )}
          </div>
        )}
        {loading && <div style={{ color: 'var(--text-secondary)', margin: '10px 0' }}>Loading repositories…</div>}
        {checkingLicenses && !loading && (
          <div style={{ color: 'var(--text-secondary)', margin: '10px 0', fontSize: '14px' }}>
            <span style={{ color: 'var(--accent)' }}>◇</span> Checking license statuses…
          </div>
        )}

        {/* Global Rate Limit Warning */}
        {licenseSummary && licenseSummary.rate_limited > 0 && (
          <div style={{
            margin: '16px 0',
            padding: '16px 18px',
            backgroundColor: 'var(--status-none-bg)',
            borderRadius: '10px',
            border: '1px solid rgba(255, 158, 171, 0.22)',
          }}>
            <h4 style={{ margin: '0 0 10px 0', color: 'var(--status-none-fg)' }}>
              ⏳ GitHub API Rate Limit Exceeded
            </h4>
            <p style={{ margin: '0 0 12px 0', color: 'var(--text-secondary)', fontSize: '14px' }}>
              Unable to fetch license status for {licenseSummary.rate_limited} repositories due to GitHub API rate limiting.
              License status will be updated automatically when the rate limit resets.
            </p>
            <button
              onClick={listRepos}
              style={{
                padding: '8px 14px',
                background: 'var(--status-none-fg)',
                color: '#3a0e1a',
                border: 'none',
                borderRadius: '6px',
                cursor: 'pointer',
                fontSize: '12px',
                fontWeight: 700,
              }}
            >
              Retry Now
            </button>
          </div>
        )}

        {/* Repositories Needing Attention */}
        {reposNeedingAttention.length > 0 && (
          <div style={{
            margin: '16px 0',
            padding: '16px 18px',
            backgroundColor: 'var(--status-expired-bg)',
            borderRadius: '10px',
            border: '1px solid rgba(255, 216, 122, 0.22)',
          }}>
            <h4 style={{ margin: '0 0 10px 0', color: 'var(--status-expired-fg)' }}>
              ⚠ Repositories Needing Attention ({reposNeedingAttention.length})
            </h4>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
              {reposNeedingAttention.slice(0, 5).map((item, index) => {
                const chipBg = item.status === 'expired' ? 'var(--status-expired-bg-hover)' :
                               item.status === 'none' ? 'var(--status-none-bg)' :
                               item.status === 'rate_limited' ? 'var(--status-neutral-bg)' : 'var(--status-expired-bg-hover)';
                const chipFg = item.status === 'expired' ? 'var(--status-expired-fg)' :
                               item.status === 'none' ? 'var(--status-none-fg)' :
                               item.status === 'rate_limited' ? 'var(--text-secondary)' : 'var(--status-expired-fg)';
                return (
                  <span
                    key={index}
                    style={{
                      padding: '4px 10px',
                      backgroundColor: chipBg,
                      color: chipFg,
                      borderRadius: '999px',
                      fontSize: '12px',
                      border: '1px solid var(--border)',
                      fontWeight: 500,
                    }}
                    title={item.issue}
                  >
                    {item.repo}
                  </span>
                );
              })}
              {reposNeedingAttention.length > 5 && (
                <span style={{
                  padding: '4px 10px',
                  backgroundColor: 'var(--surface-2)',
                  color: 'var(--text-secondary)',
                  borderRadius: '999px',
                  fontSize: '12px',
                  border: '1px solid var(--border)',
                }}>
                  +{reposNeedingAttention.length - 5} more
                </span>
              )}
            </div>
          </div>
        )}
        {!loading && !error && (
                      <div>
              <div>
                {Object.keys(repos).length === 0 ? (
                  <div style={{
                    padding: '48px 24px',
                    textAlign: 'center',
                    backgroundColor: 'var(--surface-1)',
                    borderRadius: '12px',
                    border: '1px solid var(--border)',
                  }}>
                    {isRepoListRateLimited ? (
                      <div>
                        <h3 style={{ color: 'var(--text-primary)', marginBottom: '10px' }}>⏳ Rate Limited</h3>
                        <p style={{ color: 'var(--text-secondary)', marginBottom: '20px' }}>
                          Unable to fetch repositories due to GitHub API rate limiting.
                        </p>
                        <button
                          onClick={listRepos}
                          disabled={loading}
                          style={{
                            padding: '10px 20px',
                            background: loading ? 'var(--surface-3)' : 'var(--accent)',
                            color: loading ? 'var(--text-secondary)' : 'var(--accent-fg)',
                            border: 'none',
                            borderRadius: '6px',
                            cursor: loading ? 'not-allowed' : 'pointer',
                            fontSize: '14px',
                            fontWeight: 700,
                          }}
                        >
                          {loading ? 'Retrying…' : 'Retry Fetching Repositories'}
                        </button>
                      </div>
                    ) : (
                      <p style={{ color: 'var(--text-secondary)', margin: 0 }}>No repositories found.</p>
                    )}
                  </div>
                ) : (() => {
                  const headerCellStyle = {
                    textAlign: 'left',
                    padding: '12px 16px',
                    borderBottom: '1px solid var(--border-strong)',
                    cursor: 'pointer',
                    userSelect: 'none',
                    whiteSpace: 'nowrap',
                    color: 'var(--text-secondary)',
                    fontSize: '11px',
                    fontWeight: 600,
                    textTransform: 'uppercase',
                    letterSpacing: '0.06em',
                  };
                  const filterCellStyle = {
                    padding: '8px 12px 10px',
                    borderBottom: '1px solid var(--border)',
                    background: 'var(--surface-2)',
                  };
                  const filterControlStyle = {
                    width: '100%',
                    fontSize: '12px',
                    padding: '6px 8px',
                    background: 'var(--surface-1)',
                    color: 'var(--text-primary)',
                    border: '1px solid var(--border)',
                    borderRadius: '4px',
                    boxSizing: 'border-box',
                  };
                  const allRows = buildInstallationRows();
                  // Archived repos are hidden unless the "Show archived" toggle is on
                  // or the Status filter is explicitly set to "Archived".
                  const baseRows = allRows
                    .filter((r) => (showArchived || columnFilters.status === 'archived' ? true : !r.archived))
                    .filter((r) => !needsPostInstallOnly || r.needsPostInstall);
                  const archivedCount = allRows.filter((r) => r.archived).length;
                  const needsPostInstallCount = allRows.filter((r) => r.needsPostInstall).length;
                  const rowsAfterColumnFilters = baseRows.filter(matchesColumnFilters);
                  const visibleRows = sortRows(rowsAfterColumnFilters.filter(matchesGlobalSearch));

                  // Dropdown options come from rows that match every active filter
                  // except the one being populated — so each dropdown shows only
                  // the values still reachable given the rest of the filters.
                  const optionsFor = (key) => {
                    const f = { ...columnFilters, [key]: '' };
                    const others = baseRows.filter((r) => {
                      if (f.repo && !r.repo.toLowerCase().includes(f.repo.trim().toLowerCase())) return false;
                      if (f.institution && !(r.institution || '').toLowerCase().includes(f.institution.trim().toLowerCase())) return false;
                      if (f.organization && r.organization !== f.organization) return false;
                      if (f.rhoVersion && r.rhoVersion !== f.rhoVersion) return false;
                      if (f.jurisdiction && r.jurisdiction !== f.jurisdiction) return false;
                      if (f.country && r.country !== f.country) return false;
                      if (f.stage && r.stage !== f.stage) return false;
                      if (f.useCase && r.useCase !== f.useCase) return false;
                      if (f.status === 'archived') { if (!r.archived) return false; }
                      else if (f.status && r.statusKey !== f.status) return false;
                      return true;
                    });
                    return uniqueValues(others, key);
                  };

                  return (
                    <>
                      <style>{`
                        .repo-row { transition: background-color 0.15s ease; color: var(--text-primary); cursor: pointer; }
                        .repo-row td { padding: 14px 16px; border-bottom: 1px solid var(--border); }
                        .repo-row:nth-child(odd) { background-color: var(--surface-1); }
                        .repo-row:nth-child(even) { background-color: var(--surface-2); }
                        .repo-row:hover { background-color: var(--surface-3) !important; }
                        .status-pill {
                          display: inline-block;
                          padding: 4px 12px;
                          border-radius: 999px;
                          font-size: 12px;
                          font-weight: 600;
                          line-height: 1.4;
                          white-space: nowrap;
                        }
                      `}</style>

                      <div style={{
                        marginTop: '24px',
                        marginBottom: '16px',
                        display: 'flex',
                        flexWrap: 'wrap',
                        gap: '12px',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                      }}>
                        <div style={{ color: 'var(--text-secondary)', fontSize: '13px' }}>
                          Showing <strong style={{ color: 'var(--text-primary)' }}>{visibleRows.length}</strong>
                          {visibleRows.length !== baseRows.length ? ` of ${baseRows.length}` : ''} installation{baseRows.length === 1 ? '' : 's'}
                          {showArchived && archivedCount > 0 && (
                            <span style={{ marginLeft: 6, color: 'var(--text-tertiary)' }}>(incl. {archivedCount} archived)</span>
                          )}
                          {(Object.values(columnFilters).some(Boolean) || repoSearch.trim()) && (
                            <button
                              onClick={() => { setColumnFilters({ repo: '', institution: '', organization: '', rhoVersion: '', jurisdiction: '', country: '', stage: '', useCase: '', status: '' }); setRepoSearch(''); }}
                              style={{
                                marginLeft: '12px',
                                padding: '4px 10px',
                                background: 'transparent',
                                color: 'var(--text-secondary)',
                                border: '1px solid var(--border-strong)',
                                borderRadius: '999px',
                                cursor: 'pointer',
                                fontSize: '12px',
                              }}
                            >
                              Clear filters
                            </button>
                          )}
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'nowrap' }}>
                          <label
                            title="Show only installations whose post-installation checklist is not complete (amber left border)"
                            style={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: '8px',
                              fontSize: '13px',
                              color: 'var(--text-secondary)',
                              cursor: 'pointer',
                              userSelect: 'none',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            <input
                              type="checkbox"
                              checked={needsPostInstallOnly}
                              onChange={(e) => setNeedsPostInstallOnly(e.target.checked)}
                              style={{ width: '16px', height: '16px', accentColor: 'var(--status-expired-fg)', cursor: 'pointer' }}
                            />
                            <span style={{ display: 'inline-block', width: '4px', height: '14px', background: 'var(--status-expired-fg)', borderRadius: '1px' }} />
                            Post-install pending{needsPostInstallCount > 0 ? ` (${needsPostInstallCount})` : ''}
                          </label>
                          <label
                            title="Archived repositories are hidden by default"
                            style={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: '8px',
                              fontSize: '13px',
                              color: 'var(--text-secondary)',
                              cursor: 'pointer',
                              userSelect: 'none',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            <input
                              type="checkbox"
                              checked={showArchived}
                              onChange={(e) => setShowArchived(e.target.checked)}
                              style={{ width: '16px', height: '16px', accentColor: 'var(--color-bit-blue)', cursor: 'pointer' }}
                            />
                            Show archived{archivedCount > 0 ? ` (${archivedCount})` : ''}
                          </label>
                          <input
                            type="text"
                            placeholder="Search installations…"
                            value={repoSearch}
                            onChange={(e) => setRepoSearch(e.target.value)}
                            style={{
                              width: '320px',
                              maxWidth: '100%',
                              fontSize: '14px',
                              boxSizing: 'border-box',
                            }}
                          />
                        </div>
                      </div>

                      <div
                        style={{
                          maxHeight: '640px',
                          overflowY: 'auto',
                          overflowX: 'auto',
                          border: '1px solid var(--border)',
                          borderRadius: '10px',
                          background: 'var(--surface-1)',
                          boxShadow: '0 1px 0 rgba(255,255,255,0.04) inset, 0 8px 24px -12px rgba(0,0,0,0.5)',
                        }}
                      >
                        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px' }}>
                          <thead style={{ position: 'sticky', top: 0, zIndex: 1, background: 'var(--surface-2)' }}>
                            <tr>
                              <th style={headerCellStyle} onClick={() => toggleSort('repo')}>Repository{sortIndicator('repo')}</th>
                              <th style={headerCellStyle} onClick={() => toggleSort('created')} title="When the customer repository was created on GitHub">Created{sortIndicator('created')}</th>
                              <th style={headerCellStyle} onClick={() => toggleSort('institution')}>Institution{sortIndicator('institution')}</th>
                              <th style={headerCellStyle} onClick={() => toggleSort('organization')}>Distributor{sortIndicator('organization')}</th>
                              <th style={headerCellStyle} onClick={() => toggleSort('rhoVersion')}>Rho Version{sortIndicator('rhoVersion')}</th>
                              <th style={headerCellStyle} onClick={() => toggleSort('jurisdiction')}>Jurisdiction{sortIndicator('jurisdiction')}</th>
                              <th style={headerCellStyle} onClick={() => toggleSort('country')}>Country{sortIndicator('country')}</th>
                              <th style={headerCellStyle} onClick={() => toggleSort('stage')}>Stage{sortIndicator('stage')}</th>
                              <th style={headerCellStyle} onClick={() => toggleSort('useCase')}>Use Case{sortIndicator('useCase')}</th>
                              <th style={headerCellStyle} onClick={() => toggleSort('status')}>Status{sortIndicator('status')}</th>
                              <th style={headerCellStyle} onClick={() => toggleSort('expires')}>Expires{sortIndicator('expires')}</th>
                              <th style={headerCellStyle} onClick={() => toggleSort('remaining')}>Time Remaining{sortIndicator('remaining')}</th>
                              <th style={headerCellStyle} onClick={() => toggleSort('usage')} title={usageSyncedAt ? `Self-reported by each site via Mixpanel. Last sync ${formatDateTime(usageSyncedAt)}` : 'Self-reported by each site via Mixpanel'}>X-rays Used{sortIndicator('usage')}</th>
                            </tr>
                            <tr>
                              <th style={filterCellStyle}>
                                <input
                                  type="text"
                                  placeholder="Filter…"
                                  value={columnFilters.repo}
                                  onChange={(e) => setColumnFilters({ ...columnFilters, repo: e.target.value })}
                                  style={filterControlStyle}
                                />
                              </th>
                              <th style={filterCellStyle}></th>
                              <th style={filterCellStyle}>
                                <input
                                  type="text"
                                  placeholder="Filter…"
                                  value={columnFilters.institution}
                                  onChange={(e) => setColumnFilters({ ...columnFilters, institution: e.target.value })}
                                  style={filterControlStyle}
                                />
                              </th>
                              <th style={filterCellStyle}>
                                <select
                                  value={columnFilters.organization}
                                  onChange={(e) => setColumnFilters({ ...columnFilters, organization: e.target.value })}
                                  style={filterControlStyle}
                                >
                                  <option value="">All</option>
                                  {optionsFor('organization').map((v) => <option key={v} value={v}>{v}</option>)}
                                </select>
                              </th>
                              <th style={filterCellStyle}>
                                <select
                                  value={columnFilters.rhoVersion}
                                  onChange={(e) => setColumnFilters({ ...columnFilters, rhoVersion: e.target.value })}
                                  style={filterControlStyle}
                                >
                                  <option value="">All</option>
                                  {optionsFor('rhoVersion').map((v) => <option key={v} value={v}>{v}</option>)}
                                </select>
                              </th>
                              <th style={filterCellStyle}>
                                <select
                                  value={columnFilters.jurisdiction}
                                  onChange={(e) => setColumnFilters({ ...columnFilters, jurisdiction: e.target.value })}
                                  style={filterControlStyle}
                                >
                                  <option value="">All</option>
                                  {optionsFor('jurisdiction').map((v) => <option key={v} value={v}>{v}</option>)}
                                </select>
                              </th>
                              <th style={filterCellStyle}>
                                <select
                                  value={columnFilters.country}
                                  onChange={(e) => setColumnFilters({ ...columnFilters, country: e.target.value })}
                                  style={filterControlStyle}
                                >
                                  <option value="">All</option>
                                  {optionsFor('country').map((v) => <option key={v} value={v}>{v}</option>)}
                                </select>
                              </th>
                              <th style={filterCellStyle}>
                                <select
                                  value={columnFilters.stage}
                                  onChange={(e) => setColumnFilters({ ...columnFilters, stage: e.target.value })}
                                  style={filterControlStyle}
                                >
                                  <option value="">All</option>
                                  {optionsFor('stage').map((v) => <option key={v} value={v}>{v}</option>)}
                                </select>
                              </th>
                              <th style={filterCellStyle}>
                                <select
                                  value={columnFilters.useCase}
                                  onChange={(e) => setColumnFilters({ ...columnFilters, useCase: e.target.value })}
                                  style={filterControlStyle}
                                >
                                  <option value="">All</option>
                                  {optionsFor('useCase').map((v) => <option key={v} value={v}>{v}</option>)}
                                </select>
                              </th>
                              <th style={filterCellStyle}>
                                <select
                                  value={columnFilters.status}
                                  onChange={(e) => setColumnFilters({ ...columnFilters, status: e.target.value })}
                                  style={filterControlStyle}
                                >
                                  <option value="">All</option>
                                  <option value="valid">Valid</option>
                                  <option value="expired">Expired</option>
                                  <option value="none">No License</option>
                                  <option value="rate_limited">Rate Limited</option>
                                  <option value="error">Error</option>
                                  <option value="unknown">Checking…</option>
                                  <option value="archived">Archived</option>
                                </select>
                              </th>
                              <th style={filterCellStyle}></th>
                              <th style={filterCellStyle}></th>
                              <th style={filterCellStyle}></th>
                            </tr>
                          </thead>
                          <tbody>
                            {visibleRows.length === 0 ? (
                              <tr>
                                <td colSpan={13} style={{ padding: '24px', textAlign: 'center', color: 'var(--text-secondary)' }}>
                                  No installations match the current filters.
                                </td>
                              </tr>
                            ) : visibleRows.map((row, index) => {
                              const palette = ROW_PALETTES[row.statusKey] || ROW_PALETTES.unknown;
                              return (
                                <tr
                                  key={`${row.organization}::${row.repo}::${index}`}
                                  className="repo-row"
                                  data-status={row.statusKey}
                                  onClick={() => navigate(`/config/${encodeURIComponent(row.organization)}/${encodeURIComponent(row.repo)}`)}
                                  style={row.archived ? { opacity: 0.62 } : undefined}
                                  title={row.needsPostInstall
                                    ? `Post-installation ${row.postInstall.status === 'none' ? 'not started' : 'incomplete'}. Outstanding: ${(row.postInstall.missing || []).join(', ') || 'see Post-installation tab'}`
                                    : undefined}
                                >
                                  <td style={row.needsPostInstall ? { boxShadow: 'inset 4px 0 0 var(--status-expired-fg)' } : undefined}>
                                    <strong title={row.repo}>{row.repo.replace(/^rho-customer-/, '')}</strong>
                                    {row.needsPostInstall && (
                                      <span style={{
                                        marginLeft: '8px',
                                        fontSize: '10px',
                                        fontWeight: 700,
                                        letterSpacing: '0.04em',
                                        textTransform: 'uppercase',
                                        color: 'var(--status-expired-fg)',
                                        border: '1px solid var(--status-expired-fg)',
                                        borderRadius: '999px',
                                        padding: '2px 8px',
                                        whiteSpace: 'nowrap',
                                      }}>{row.postInstall.status === 'none' ? 'Post-install not started' : 'Post-install pending'}</span>
                                    )}
                                    {row.archived && (
                                      <span style={{
                                        marginLeft: '8px',
                                        fontSize: '10px',
                                        fontWeight: 700,
                                        letterSpacing: '0.04em',
                                        textTransform: 'uppercase',
                                        color: 'var(--text-secondary)',
                                        border: '1px solid var(--border-strong)',
                                        borderRadius: '999px',
                                        padding: '2px 8px',
                                      }}>Archived</span>
                                    )}
                                  </td>
                                  <td title={row.createdAt ? formatDateTime(row.createdAt) : 'Not in cache yet; use Refresh Cache'} style={{ whiteSpace: 'nowrap' }}>
                                    {row.createdAt ? formatDate(row.createdAt) : '—'}
                                  </td>
                                  <td>{row.institution || '—'}</td>
                                  <td>{row.organization || '—'}</td>
                                  <td>{row.rhoVersion || '—'}</td>
                                  <td>{row.jurisdiction || '—'}</td>
                                  <td>{row.country || '—'}</td>
                                  <td>{row.stage || '—'}</td>
                                  <td>{row.useCase || '—'}</td>
                                  <td>
                                    {row.archived ? (
                                      <span
                                        className="status-pill"
                                        style={{
                                          backgroundColor: 'var(--surface-3)',
                                          color: 'var(--text-secondary)',
                                          border: '1px solid var(--border-strong)',
                                        }}
                                      >
                                        Archived
                                      </span>
                                    ) : (
                                      <span
                                        className="status-pill"
                                        style={{
                                          backgroundColor: palette.bg,
                                          color: palette.text,
                                          border: '1px solid var(--border-strong)',
                                        }}
                                      >
                                        {palette.label}
                                      </span>
                                    )}
                                  </td>
                                  <td>{formatExpiry(row.repo)}</td>
                                  <td>{formatDaysRemaining(row.repo)}</td>
                                  <td>{renderUsageCell(row)}</td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    </>
                  );
                })()}
            </div>
          </div>
        )}
        {timeout && loading && (
          <div style={{ color: 'var(--status-expired-fg)', margin: '10px 0', fontSize: '13px' }}>
            Still loading repositories… (Lambda may be slow)
          </div>
        )}
      </div>

      {/* Create Repo Modal */}
      {showCreateRepo && (
        <div style={modalBackdropStyle} onClick={() => { setShowCreateRepo(false); setNewRepoName(""); setCreateRepoError(null); setRepoNameError(''); }}>
          <div style={modalDialogStyle} onClick={e => e.stopPropagation()}>
            <h2 style={{ margin: 0, marginBottom: 10 }}>Create New Repository</h2>
            <div style={{ fontSize: '15px', marginBottom: '8px', color: 'var(--text-secondary)' }}>
              <strong style={{ color: 'var(--text-primary)' }}>Full Repo Name:</strong>{' '}
              <span style={{ background: 'var(--surface-2)', color: 'var(--accent)', padding: '3px 8px', borderRadius: '4px', fontFamily: 'monospace', border: '1px solid var(--border)' }}>rho-customer-{newRepoName || '<name>'}</span>
            </div>
            <form onSubmit={handleCreateRepo} style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 0 }}>
                <span style={{ background: 'var(--surface-3)', color: 'var(--text-secondary)', padding: '10px 12px', borderRadius: '6px 0 0 6px', fontFamily: 'monospace', border: '1px solid var(--border)', borderRight: 'none', fontSize: '15px' }}>rho-customer-</span>
                                <input
                  type="text"
                  value={newRepoName}
                  onChange={handleRepoNameChange}
                  onKeyDown={(e) => {
                    // Prevent typing of invalid characters
                    const invalidChars = /[^a-z0-9-]/;
                    if (invalidChars.test(e.key) && !['Backspace', 'Delete', 'Tab', 'Enter', 'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown'].includes(e.key)) {
                      e.preventDefault();
                    }
                  }}
                  placeholder="Enter customer/repo name"
                  style={{
                    padding: '10px 12px',
                    borderRadius: '0 6px 6px 0',
                    fontSize: '15px',
                    flex: 1,
                  }}
                  required
                  disabled={creatingRepo}
                  autoFocus
                />
                </div>
                {repoNameError && (
                  <div style={{ color: 'var(--status-none-fg)', fontSize: '13px', marginTop: '4px' }}>
                    {repoNameError}
                  </div>
                )}
                <div style={{ fontSize: '12px', color: 'var(--text-tertiary)', marginTop: '4px' }}>
                  Use only lowercase letters, numbers, and dashes (e.g., my-company, customer-123)
                </div>
                <div style={{ display: 'flex', gap: '10px', marginTop: '8px' }}>
                <button
                  type="submit"
                  disabled={creatingRepo || !newRepoName || !!repoNameError}
                  style={{
                    padding: '10px 20px',
                    background: creatingRepo ? 'var(--surface-3)' : 'var(--accent)',
                    color: creatingRepo ? 'var(--text-secondary)' : 'var(--accent-fg)',
                    border: 'none',
                    borderRadius: '6px',
                    cursor: creatingRepo ? 'not-allowed' : 'pointer',
                    fontWeight: 700,
                    fontSize: '14px',
                    letterSpacing: '0.02em',
                  }}
                >
                  {creatingRepo ? 'Creating…' : 'Create'}
                </button>
                                  <button
                    type="button"
                    onClick={() => { setShowCreateRepo(false); setNewRepoName(""); setCreateRepoError(null); setRepoNameError(''); }}
                    style={{
                    padding: '10px 20px',
                    background: 'transparent',
                    color: 'var(--text-secondary)',
                    border: '1px solid var(--border-strong)',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    fontWeight: 600,
                    fontSize: '14px',
                  }}
                  disabled={creatingRepo}
                >
                  Cancel
                </button>
              </div>
              {createRepoError && (
                <span style={{ color: 'var(--status-none-fg)', marginTop: '6px', fontSize: '14px' }}>{createRepoError}</span>
              )}
            </form>
          </div>
        </div>
      )}

      {/* Custom Tooltip */}
      {hoveredRepo && (
        <div
          style={{
            position: 'fixed',
            left: tooltipPosition.x,
            top: tooltipPosition.y,
            backgroundColor: '#333',
            color: 'white',
            padding: '8px 12px',
            borderRadius: '4px',
            fontSize: '12px',
            zIndex: 1000,
            pointerEvents: 'none',
            boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
            maxWidth: '250px',
            whiteSpace: 'nowrap'
          }}
        >
          {getLicenseTooltip(hoveredRepo)}
        </div>
      )}
    </div>
  );
}